import { PROTECTED_TECHNICAL_TERMS } from '../glossary';
import { type TranslationTree } from '../types';

export const enTranslations: TranslationTree = {
  common: {
    language: {
      label: 'Language',
      english: 'English',
      indonesian: 'Bahasa Indonesia',
      code: {
        en: 'EN',
        id: 'ID',
      },
    },
    actions: {
      download: PROTECTED_TECHNICAL_TERMS.download,
      upload: PROTECTED_TECHNICAL_TERMS.upload,
      export: PROTECTED_TECHNICAL_TERMS.export,
      import: PROTECTED_TECHNICAL_TERMS.import,
      back: 'Back',
      logout: 'Logout',
      collapse: 'Collapse',
      expandSidebar: 'Expand Sidebar',
      collapseSidebar: 'Collapse Sidebar',
      close: 'Close',
    },
    terms: {
      greenBean: PROTECTED_TECHNICAL_TERMS.greenBean,
      roastedBulk: PROTECTED_TECHNICAL_TERMS.roastedBulk,
      finishedGood: PROTECTED_TECHNICAL_TERMS.finishedGood,
    },
    user: {
      fallbackName: 'User',
    },
    header: {
      replayTour: 'Replay tour',
      replayTourError: 'Could not restart the tour. Please try again.',
    },
    dateRange: {
      label: 'Date Range',
      customRange: 'Custom Range',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      thisQuarter: 'This Quarter',
      customSection: 'Custom Range',
      startDate: 'Start Date',
      endDate: 'End Date',
    },
    pagination: {
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      total: 'Total',
      label: 'Pagination',
      showing: '{{to}} of {{total}}',
      empty: 'No results',
      pageOf: 'Page {{page}} of {{totalPages}}',
      goToPage: 'Go to page {{page}}',
      perPage: 'Per page',
    },
    errorBoundary: {
      title: 'Something went wrong',
      unexpected: 'An unexpected error occurred.',
      reload: 'Reload Page',
    },
    modal: {
      closeAriaLabel: 'Close modal',
    },
    table: {
      na: 'N/A',
      unknown: 'Unknown',
      viewSupplier: 'View Supplier',
    },
    fileName: {
      roastProfilePrefix: 'RoastProfile',
    },
    statuses: {
      pending: 'pending',
    },
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    cancel: 'Cancel',
    create: 'Create',
    close: 'Close',
    save: 'Save',
    loading: 'Loading…',
    search: 'Search',
  },
  errors: {
    common: {
      unknownError: 'Unknown error',
      loadDataFailed: 'Failed to load data',
    },
    forbidden: {
      title: 'No access',
      inlineTitle: 'Restricted section',
      description: "You don't have permission to view this content. Ask an administrator if you need access.",
      inlineDescription: "You don't have permission to view this section.",
      pageTitle: 'This page is restricted',
      pageDescription: "Your role doesn't include access to this area. Pick another section from the sidebar, or ask an administrator to update your permissions.",
      backToDashboard: 'Back to dashboard',
      goBack: 'Go back',
    },
  },
  header: {
    language: {
      srLabel: 'Language selector',
    },
  },
  sidebar: {
    dashboard: 'Dashboard',
    purchasing: 'Purchasing',
    purchaseOrder: 'Purchase Order',
    supplierMaster: 'Supplier Master',
    lotList: 'Lot List',
    sampleList: 'Sample List',
    inventory: 'Inventory',
    production: 'Production',
    workOrder: 'Work Order',
    masterProfile: 'Master Profile',
    artisanSync: 'Artisan Sync',
    qualityControl: 'Quality Control',
    cuppingSession: 'Cupping Session',
    selling: 'Selling',
    salesOrder: 'Sales Order',
    customerMaster: 'Customer Master',
    report: 'Report',
    generalReport: 'General Report',
    settings: 'Settings',
    general: 'General',
    member: 'Member',
    adminMember: 'Admin Member',
    roastingMachine: 'Roasting Machine',
    roastMaster: 'Roast Master',
    activityLogs: 'Activity Logs',
    logoAlt: 'Qrema Logo',
    subscription: 'Subscription',
    vouchers: 'Vouchers',
    companies_subscriptions: 'Companies & Subscriptions',
    adminConsole: 'Admin Console',
  },
  adminConsole: {
    badge: {
      title: 'Admin Console',
      subtitle: 'Platform-level controls',
    },
    nav: {
      members: 'Admin Members',
      vouchers: 'Vouchers',
      companies: 'Companies & Subscriptions',
      lynkPayments: 'Transaction History',
    },
    backToApp: 'Back to app',
  },
  lynkPayments: {
    title: 'Lynk.id Transaction History',
    subtitle: 'Read-only audit log of Lynk.id payment webhook transactions.',
    searchPlaceholder: 'Search by email, message ID, ref ID, or item...',
    empty: 'No Lynk transactions match the current filters.',
    tierLabel: 'Tier',
    unknownTier: 'Unknown tier',
    noError: 'No error',
    stats: {
      total: 'Total Transactions',
      applied: 'Applied Successfully',
      failed: 'Failed Processing',
    },
    filters: {
      state: 'Apply Status',
      all: 'All',
      applied: 'Applied',
      failed: 'Failed',
    },
    status: {
      applied: 'Applied',
      failed: 'Failed',
    },
    columns: {
      receivedAt: 'Received At',
      customerEmail: 'Customer Email',
      item: 'Item',
      amount: 'Amount',
      status: 'Status',
      messageId: 'Message ID',
      refId: 'Ref ID',
      event: 'Event',
      company: 'Company',
      applyError: 'Apply Error',
    },
    detail: {
      title: 'Lynk Event Detail',
      rawPayload: 'Raw Payload',
    },
  },
  activityLogs: {
    title: 'Activity Logs',
    description: 'Audit trail of all create, update, and delete operations across business modules.',
    empty: 'No activity recorded for these filters.',
    loadError: 'Failed to load activity logs.',
    viewAll: 'View all',
    backToSettings: 'Back to Settings',
    filters: {
      module: 'Module',
      allModules: 'All modules',
      startDate: 'Start date',
      endDate: 'End date',
    },
    columns: {
      time: 'Time',
      module: 'Module',
      type: 'Type',
      reference: 'Reference',
      user: 'User',
    },
    types: {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
    },
  },
  settings: {
    title: 'Settings',
    subscription: 'Subscription',
    vouchers: 'Vouchers',
    companies_subscriptions: 'Companies & Subscriptions',
    personalPreferences: 'Personal Preferences',
    personalPreferencesDescription: 'Manage your local application preferences.',
    defaultUnit: 'Default Unit of Measure',
    defaultUnitPlaceholder: 'e.g. pcs, kg',
    theme: 'Theme',
    themeLight: 'Light',
    themeLightDescription: 'Always use light appearance',
    themeDark: 'Dark',
    themeDarkDescription: 'Always use dark appearance',
    themeAuto: 'Auto (System)',
    themeAutoDescription: 'Follow your device preference',
    themeCurrentStatus: 'Current mode: {{mode}} · Applied appearance: {{appearance}}{{syncing}}',
    themeSyncingSuffix: ' · Syncing…',
    emailNotifications: {
      label: 'Email notifications',
      description: 'When enabled, work-order reminders will also be sent to your email inbox. Web notifications are always on.',
      enabledToast: 'Email notifications enabled',
      disabledToast: 'Email notifications disabled',
      errorToast: 'Failed to update preference',
    },
    changePassword: {
      title: 'Change Password',
      subtitle: 'Update your account password. You must provide your current password to confirm.',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      submit: 'Update Password',
      submitting: 'Updating…',
      success: 'Password updated successfully',
      errors: {
        currentRequired: 'Current password is required',
        newMinLength: 'New password must be at least 6 characters',
        confirmMismatch: "Passwords don't match",
        sameAsCurrent: 'New password must be different from current password',
        currentIncorrect: 'Current password is incorrect',
        updateFailed: 'Failed to update password',
      },
    },
  },
  auth: {
    brandLogoAlt: 'Qrema Logo',
    toast: {
      welcomeBack: 'Welcome back!',
      passwordUpdated: 'Password updated successfully. Please sign in.',
    },
    validation: {
      invalidEmail: 'Please enter a valid email address',
      passwordRequired: 'Password is required',
      companyNameShort: 'Company name is too short',
      fullNameRequired: 'Please enter your full name',
      phoneMinLength: 'Phone number must be at least 4 digits',
      passwordMinLength: 'Password must be at least 6 characters',
      passwordUppercase: 'Password must contain at least one uppercase letter',
      passwordMismatch: "Passwords don't match",
      emailAlreadyInUse: 'This email is already in use',
      otpLength: 'OTP must be 6 digits',
      confirmPasswordMinLength: 'Confirm password must be at least 6 characters',
      resetTokenInvalid: 'Reset token is invalid',
      invalidEmailSimple: 'Invalid email address',
    },
    login: {
      title: 'Sign in to your account',
      newHere: 'New here?',
      registerCompany: 'Register your company',
      emailAddress: 'Email address',
      emailPlaceholder: 'name@company.com',
      password: 'Password',
      forgotPassword: 'Forgot password?',
      passwordPlaceholder: '••••••••',
      submit: 'Sign in',
    },
    register: {
      title: 'Create account',
      alreadyHaveCompany: 'Already have a company?',
      signInHere: 'Sign in here',
      companyName: 'Company Name',
      fullName: 'Full Name',
      emailAddress: 'Email Address',
      companyNamePlaceholder: 'Acme Corp',
      fullNamePlaceholder: 'John Doe',
      emailPlaceholder: 'john@acme.com',
      phoneNumber: 'Phone Number',
      phoneNumberPlaceholder: '812 3456 7890',
      password: 'Password',
      confirm: 'Confirm Password',
      next: 'Next',
      voucherCode: 'Voucher Code',
      voucherCodeOptional: 'Voucher Code (optional)',
      voucherCodePlaceholder: 'XXXX-XXXX-XXXX',
      voucherCodeHint: 'Have a voucher? Add extra months on top of your free trial.',
      trialBadgeTitle: '1-month free Pro trial included',
      trialBadgeSubtitle: 'Register now for 1-month free Pro trial.',
      voucherInvalid: 'Voucher code is invalid.',
      voucherExpired: 'This voucher has expired.',
      voucherExhausted: 'This voucher has reached its redemption limit.',
      voucherNotFound: 'Voucher code not found.',
    },
    forgotPassword: {
      title: 'Forgot your password?',
      subtitle: 'Enter your email and we will send a reset link',
      success: 'If an account exists for {{email}}, a reset link has been sent.',
      emailAddress: 'Email address',
      submit: 'Submit',
      submitting: 'Submitting',
      rememberedPassword: 'Remembered your password?',
      signIn: 'Sign in',
    },
    resetPassword: {
      title: 'Reset your password',
      subtitle: 'Enter a new password for your account',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      updatePassword: 'Update password',
      backToSignIn: 'Back to sign in',
    },
    verifyOtp: {
      title: 'Verify your email',
      subtitle: 'We sent a verification code to {{email}}',
      verificationCode: 'Verification Code',
      submit: 'Verify & Register',
      resendPrompt: "Didn't receive the code?",
      resend: 'Resend code',
      resendIn: 'Resend in {{seconds}}s',
      resending: 'Sending...',
      resendSuccess: 'A new code has been sent to your email.',
      resendCooldown: 'Please wait {{seconds}}s before requesting another code.',
    },
    common: {
      success: 'Success!',
      failed: 'Failed',
      confirm: 'Confirm',
      cancel: 'Cancel',
      closeModal: 'Close modal',
    },
  },
  dashboard: {
    sessionExpired: 'Session expired. Please login again.',
    syncThemeWarning: 'Failed to sync theme preference. Will retry automatically.',
    syncLanguageWarning: 'Failed to sync language preference. Will retry automatically.',
    syncPreferencesWarning: 'Failed to sync dashboard preferences.',
    page: {
      title: 'Dashboard',
      subtitle: 'Overview of your business metrics',
      syncingPreferences: 'Syncing dashboard preferences...',
      dashboardMenu: 'Dashboard Menu',
      connectionError: 'Connection Error',
      errorOccurred: 'Error Occurred',
      modalCreateSalesOrder: 'Create Sales Order',
      modalCreatePurchaseOrder: 'Create Purchase Order',
      modalCreateWorkOrder: 'Create Work Order',
      createSalesOrderError: 'Failed to create sales order',
      createPurchaseOrderError: 'Failed to create purchase order',
      createWorkOrderError: 'Failed to create work order',
      supplierNotFound: 'Supplier not found.',
      supplierLoadError: 'Failed to load supplier details.',
    },
    greenBeanInfographics: {
      error: 'Failed to load green bean species and variety infographics.',
      empty: 'No green bean profiles available to display.',
      title: 'Green Bean Species & Variety',
      subtitle: 'Distribution breakdown of species and varietals',
      species: 'Species',
      varietals: 'Varietals',
      profileCount: '{{count}} profile',
    },
    greenBeanLotsModal: {
      title: `${PROTECTED_TECHNICAL_TERMS.greenBean} Lots`,
      total: 'Total',
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      loading: 'Loading...',
      errorLoad: 'Failed to load green bean lots.',
      empty: 'No green bean lots found.',
      viewSupplier: 'View Supplier',
      noSupplierSelected: 'No supplier selected.',
      supplierNotFound: 'Supplier not found.',
      supplierLoadFailed: 'Failed to load supplier details.',
      columns: {
        lot: 'Lot',
        lastPurchaseKg: 'Last Purchase (kg)',
        remainingKg: 'Remaining (kg)',
        netPrice: 'Net Price',
      },
    },
    quickActions: {
      title: 'Quick Actions',
      createSO: 'Create SO',
      salesOrder: 'Sales Order',
      createPO: 'Create PO',
      purchaseOrder: 'Purchase Order',
      createWO: 'Create WO',
      workOrder: 'Work Order',
      addProduct: 'Add Product',
      inventory: 'Inventory',
    },
    activityFeed: {
      title: 'Recent Activity',
      systemActivity: 'System Activity',
      salesOrder: 'Sales Order',
      purchaseOrder: 'Purchase Order',
      workOrder: 'Work Order',
      stockMovement: 'Stock Movement',
    },
    averageShrinkage: {
      title: 'Average Shrinkage',
      trendIncreased: 'Increased from last period',
      trendDecreased: 'Decreased from last period',
      trendStable: 'Stable from last period',
      footnote: 'Calculated from completed roasting work orders.',
    },
    financialStats: {
      revenue: 'Revenue',
      expenses: 'Expenses',
      netProfit: 'Net Profit',
      outstanding: 'Outstanding',
      greenBean: 'Green Bean',
      roasted: 'Roasted',
      finished: 'Finished',
      outstandingPending: '{{count}} pending',
      totalPrefix: 'Total:',
      financialOverview: 'Financial Overview',
      inventoryValue: 'Inventory Value',
    },
    inventoryAlerts: {
      expiringSoon: 'Expiring Soon',
      lowStock: 'Low Stock',
      lot: 'Lot',
      batch: 'Batch',
      expiry: 'Exp',
      daysSuffix: 'days',
      noExpiringItems: 'No items expiring soon.',
      noLowStockItems: 'No low stock items.',
      reorder: 'Reorder',
      restock: 'Restock',
      min: 'Min',
    },
    menu: {
      customizeTitle: 'Customize Dashboard',
      description: 'Select the sections you want to display on your main dashboard.',
      mainSections: 'Main Sections',
      quickStats: 'Quick Stats',
      activeCount: '{{count}} of {{total}} active',
      cancel: 'Cancel',
      applyChanges: 'Apply Changes',
      groupFinancialStats: 'Financial Stats',
      groupGreenBeanProfile: 'Green Bean Species & Variety',
      itemAiAssistant: 'AI Assistant',
      itemActivityFeed: 'Recent Activities',
      itemExpiringSoon: 'Expiry Soon',
      itemLowStock: 'Low Stock',
      itemAverageShrinkage: 'Average Shrinkage',
      itemQuickActions: 'Quick Actions',
      cardRevenue: 'Revenue',
      cardExpenses: 'Expenses',
      cardNetProfit: 'Net Profit',
      cardOutstandingPayment: 'Outstanding Payment',
      cardGreenBean: 'Green Bean',
      cardRoasted: 'Roasted',
      cardFinished: 'Finished',
      cardSpecies: 'Species',
      cardVarietals: 'Varietals',
    },
    expensesModal: {
      title: 'Expenses',
      loading: 'Loading expense details...',
      error: 'Failed to load purchase order expenses.',
      empty: 'No purchase orders found for this date range.',
      poNumber: 'PO Number',
      buyWeight: 'Buy Weight',
      arrivalWeight: 'Arrival Weight',
      yieldShrinkage: 'Yield Shrinkage',
      unitPrice: 'Unit Price',
      extraExpense: 'Extra Expense',
      totalCost: 'Total Cost',
    },
    roastedBulkModal: {
      title: 'Roasted Bulk Stock',
      total: 'Total',
      loading: 'Loading...',
      error: 'Failed to load roasted bulk stock.',
      empty: 'No roasted bulk stock found.',
      item: 'Item',
      roastType: 'Roast Type',
      totalStockKg: 'Total Stock (kg)',
    },
    outstandingPaymentsModal: {
      title: 'Outstanding Payments',
      loading: 'Loading outstanding payments...',
      empty: 'No outstanding payments found.',
      orderNumber: 'Order Number',
      supplier: 'Supplier',
      date: 'Date',
      total: 'Total',
      orderStatus: 'Order Status',
      paymentStatus: 'Payment Status',
      deliveryStatus: 'Delivery Status',
      actions: 'Actions',
      markAsPaid: 'Mark as Paid',
      markAsPaidTitle: 'Mark as Paid - {{orderNumber}}',
    },
    supplierModal: {
      titleWithName: 'Supplier: {{name}}',
      title: 'Supplier',
      loading: 'Loading supplier...',
      noSelected: 'No supplier selected.',
    },
    greenBeanProfile: {
      unknown: 'Unknown',
      others: 'Others',
      profileCount: '{{count}} profile',
      error: 'Failed to load green bean species and varietal infographics.',
      empty: 'No green bean profiles available to display.',
      title: 'Green Bean Species & Variety',
      subtitle: 'Distribution breakdown of species and varietals',
      species: 'Species',
      varietals: 'Varietals',
    },
  },
  production: {
    machineForm: {
      addMachine: 'Add Machine',
      addType: 'Add Type',
      machine: 'Machine',
      typeName: 'Type Name',
      sampleRoasterMachine: 'Sampel Roaster Machine',
      minimumInput: 'Minimum Input',
      maximumInput: 'Maximum Input',
      sampleCapacityHint: 'Sample roaster machine capacity must be between 50g and 300g.',
      notesPlaceholder: 'Add any internal observations or maintenance history...',
      createMachine: 'Create Machine',
      saveType: 'Save Type',
      createType: 'Create Type',
      errors: {
        minGreater: 'Minimum input cannot be greater than maximum input',
        minMaxRequired: 'Minimum and maximum input are required for Sampel Roaster Machine.',
        sampleCapacityRange: 'Please enter a sample roaster machine capacity between 50g and 300g.',
      },
      toast: {
        typeUpdated: 'Type updated successfully.',
        typeCreated: 'Type created successfully.',
        typeSaveFailed: 'Failed to save type.',
      },
    },
    roasterList: {
      title: 'Roast Master',
      searchPlaceholder: 'Search by name...',
      loading: 'Loading roasters...',
      empty: 'No roasters found.',
      confirmDelete: 'Are you sure?',
      actions: {
        add: 'Add Roaster',
      },
      modal: {
        addTitle: 'Add Roaster',
        editTitle: 'Edit Roaster',
      },
      toast: {
        created: 'Roaster created successfully.',
        updated: 'Roaster updated successfully.',
        deleted: 'Roaster deleted successfully.',
        saveFailed: 'Failed to save roaster.',
        deleteFailed: 'Failed to delete roaster.',
        duplicateName: 'This name already exists. Please use a different name.',
      },
    },
    roasterForm: {
      notesPlaceholder: 'Add internal notes for this roaster...',
      createRoaster: 'Create Roaster',
    },
    machineList: {
      title: 'Roasting Machine',
      loading: 'Loading machines...',
      empty: 'No machines found.',
      emptyTypes: 'No types found for this machine.',
      confirmDeleteMachine: 'Are you sure you want to delete this machine?',
      confirmDeleteType: 'Are you sure you want to delete this machine type?',
      actions: {
        addMachineType: 'Add Machine / Type',
        editMachine: 'Edit Machine',
        deleteMachine: 'Delete Machine',
        editType: 'Edit Type',
        deleteType: 'Delete Type',
      },
      modal: {
        addMachineType: 'Add Machine / Type',
        editMachine: 'Edit Machine',
        editType: 'Edit Type',
      },
      table: {
        machineName: 'Machine Name',
        sampleRoaster: 'Sampel Roaster',
        actions: 'Actions',
        typeName: 'Type Name',
        status: 'Status',
        minInput: 'Min Input',
        maxInput: 'Max Input',
      },
      values: {
        yes: 'Yes',
        no: 'No',
        active: 'Active',
        inactive: 'Inactive',
      },
      toast: {
        machineDeleted: 'Machine deleted successfully.',
        machineDeleteFailed: 'Failed to delete machine.',
        typeDeleted: 'Type deleted successfully.',
        typeDeleteFailed: 'Failed to delete type.',
        machineUpdated: 'Machine updated successfully.',
        machineCreated: 'Machine created successfully.',
        machineSaveFailed: 'Failed to save machine.',
      },
    },
  },
  purchasing: {
    sampleList: {
      title: 'Sample List',
      loading: 'Loading samples...',
      empty: 'No samples found.',
      filters: {
        search: 'Search',
        searchPlaceholder: 'Search by Sample #, Item Name or Code',
        date: 'Date',
      },
      table: {
        sampleNumber: 'Sample Number',
        item: 'Item',
        notes: 'Notes',
        source: 'Source',
        greenBeanProfile: 'Green Bean Profile',
        roastProfile: 'Roast Profile',
        tasteProfile: 'Cupping Profile',
        receivedDate: 'Received Date',
        qualityControl: 'Quality Control',
        supplier: 'Supplier',
      },
      values: {
        unknownItem: 'Unknown Item',
        fillProfileInLot: 'Fill profile in lot',
      },
      pagination: {
        showing: 'Showing {{from}} to {{to}} of {{total}} results',
      },
      modal: {
        greenBeanQc: 'Green Bean QC',
        cuppingQc: 'Cupping QC',
      },
    },
    lotList: {
      title: 'Received Lots',
      loading: 'Loading lots...',
      empty: 'No lots found.',
      filters: {
        search: 'Search',
        searchPlaceholder: 'Search by Lot # or Item',
        status: 'Status',
      },
      status: {
        all: 'All',
        active: 'Active',
        depleted: 'Depleted',
      },
      table: {
        lotNumber: 'Lot Number',
        item: 'Item',
        source: 'Source',
        receivedDate: 'Received Date',
        remaining: 'Remaining',
        status: 'Status',
        greenBeanProfile: 'Green Bean Profile',
        roastProfile: 'Roast Profile',
        tasteProfile: 'Cupping Profile',
        qualityControl: 'Quality Control',
        actions: 'Actions',
      },
      values: {
        unknownItem: 'Unknown Item',
      },
      actions: {
        view: 'View',
        viewPhoto: 'View Photo',
      },
      modal: {
        greenBeanQc: 'Green Bean QC',
        cuppingQc: 'Cupping QC',
        lotPhoto: 'Lot Photo',
        workOrderReadOnly: 'Work Order — {{number}}',
      },
    },
    supplierList: {
      title: 'Supplier Master',
      loading: 'Loading suppliers...',
      empty: 'No suppliers found.',
      confirmDelete: 'Are you sure?',
      filters: {
        search: 'Search',
        searchPlaceholder: 'Search by name, email, notes...',
        status: 'Status',
      },
      status: {
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
      },
      table: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        status: 'Status',
        internalNotes: 'Internal Notes',
        actions: 'Actions',
      },
      actions: {
        add: 'Add Supplier',
        edit: 'Edit',
        delete: 'Delete',
        openWhatsApp: 'Open WhatsApp',
        call: 'Call',
      },
      modal: {
        addTitle: 'Add Supplier',
        editTitle: 'Edit Supplier',
      },
      toast: {
        deleteFailed: 'Failed to delete supplier.',
        saveFailed: 'Failed to save supplier.',
        inUse: 'This supplier cannot be deleted because it is currently in use.',
      },
    },
  },
  purchaseOrder: {
    actions: {
      edit: 'Edit',
      viewProof: 'View Proof of Payment',
      downloadProof: 'Download Proof of Payment',
    },
    table: {
      title: 'Purchase Orders',
      loading: 'Loading purchase orders...',
      errorLoad: 'Failed to load purchase orders.',
      empty: 'No purchase orders found for this supplier.',
      orderStatus: 'Order Status',
      paymentStatus: 'Payment Status',
      deliveryStatus: 'Delivery Status',
      lineItemsLots: 'Line Items & Lots',
      lotTrackingActive: 'Lot Tracking Active',
      qtyOrdered: 'Qty Ordered',
      qtyReceived: 'Qty Received',
      processing: 'Processing...',
      pendingReceipt: 'Pending Receipt',
      viewGreenBeanProfile: 'View Green Bean Profile',
      addGreenBeanProfile: 'Add Green Bean Profile',
      orderNumberShort: 'Order #',
      shrinkageKg: 'Shrinkage (kg)',
      shrinkagePercent: 'Shrinkage (%)',
      lotId: 'Lot ID',
      unitPrice: 'Unit Price',
      total: 'Total',
    },
    filters: {
      search: 'Search',
      searchPlaceholder: 'PO code or supplier...',
      order: 'Order',
      payment: 'Payment',
      delivery: 'Delivery',
      dates: 'Dates',
      dateRangeFilters: 'Date Range Filters',
      orderDate: 'Order Date',
      deliveryDate: 'Delivery Date',
      applyFilters: 'Apply Filters',
      options: {
        all: 'All',
        draft: 'Draft',
        ordered: 'Ordered',
        cancelled: 'Cancelled',
        unpaid: 'Unpaid',
        paid: 'Paid',
        refunded: 'Refunded',
        pending: 'Pending',
        shipping: 'Shipping',
        received: 'Received',
        bankTransfer: 'Bank Transfer',
        cod: 'Cash On Delivery',
      },
    },
    paymentDetails: {
      title: 'Payment Details',
      paymentStatus: 'Payment Status',
      paymentType: 'Payment Type',
      proofOfPayment: 'Proof of Payment',
      other: 'Other',
      typePaymentMethod: 'Type payment method',
      attachFile: 'Attach file',
      removeAttachment: 'Remove attachment',
      uploadFailed: 'Upload failed',
      downloadFailed: 'Failed to download attachment',
      acceptedFormats: 'Accepted: jpg, jpeg, png, pdf. Max. file size: 1 MB.',
      invalidFormat: 'Unsupported file format. Allowed: jpg, jpeg, png, pdf.',
      fileTooLarge: 'File exceeds the maximum allowed size of 1 MB.',
    },
    items: {
      addItem: 'Add Item',
      selectItem: 'Select Item',
      weight: 'Weight',
      arrival: 'Arrival',
      sampleItem: 'Sample Item',
      unitPriceGreaterThanZero: '*Unit price should be greater than 0',
      arrivalExceedsBuyWeight: '*Arrival weight cannot exceed buy weight',
    },
    deliveryDetails: {
      title: 'Delivery Details',
      actualDeliveryDate: 'Actual Delivery Date',
      deliveryStatus: 'Delivery Status',
    },
    form: {
      submit: {
        create: 'Create Purchase Order',
        save: 'Save Purchase Order',
        receive: 'Mark as Received',
        previewPo: 'Preview',
        downloadPo: 'Download PO',
        preparing: 'Preparing...',
      },
      fields: {
        supplier: 'Supplier',
        supplierPlaceholder: 'Select or type new supplier',
        supplierInternalNote: 'Supplier Internal Note',
        supplierInternalNotePlaceholder: 'Internal note for supplier...',
        orderDate: 'Order Date',
        orderStatus: 'Order Status',
        internalNotes: 'Internal Notes',
        internalNotesPlaceholder: 'Internal notes...',
      },
      generalInformation: 'General Information',
      errors: {
        supplierInactive: 'Selected supplier is inactive.',
        supplierUnresolved: 'Please pick a supplier from the list.',
        actualDeliveryDateInvalid: 'Actual Delivery Date must be later than or equal to Order Date',
        arrivalWeightRequired: 'Please fill in the Arrival Weight for all items.',
      },
      toast: {
        supplierInactive: 'Selected supplier is inactive and cannot be used for a new purchase order.',
        supplierUnresolved: 'That supplier could not be identified. Please reselect it from the list.',
        receivedSuccess: 'Purchase Order received. Inventory Lots and Samples created successfully.',
        savedSuccess: 'Purchase Order saved successfully.',
        saveFailed: 'Failed to save Purchase Order. Please try again.',
      },
      values: {
        itemWithCode: 'Item {{code}}',
        unknownItem: 'Unknown Item',
        lossDescription: 'Losses from PO {{itemName}} ({{weightDifference}} kg × {{unitPrice}})',
      },
      modals: {
        highShrinkageTitle: 'High Shrinkage Warning',
        highShrinkageWarning: 'Warning: The following items have a shrinkage greater than 30%:',
        highShrinkageItem: '{{name}}: {{shrinkage}}% shrinkage',
        highShrinkageConfirm: 'Are you sure you want to proceed?',
        unitPriceAdjustmentTitle: 'Unit Price Adjustment',
        proceed: 'Proceed',
        weightDiscrepancyTitle: 'Weight Discrepancy Detected',
        weightDiscrepancyDescription: 'The received weight differs from the order. Would you like to recalculate unit prices using:',
        totalDivReceivedWeight: 'Total ÷ Received Weight',
        weightComparison: 'Weight Comparison',
        ordered: 'Ordered',
        received: 'Received',
        priceAdjustment: 'Price Adjustment',
        keepOriginal: 'Keep Original',
        applyAdjustments: 'Apply Adjustments',
      },
    },
  },
  salesOrder: {
    payment: {
      title: 'Record Payment',
      markAsPaid: 'Mark as Paid',
      edit: 'Update payment',
      save: 'Save Payment',
      orderTotal: 'Order Total',
      statusLabel: 'Payment Status',
      typeLabel: 'Payment Method',
      refundNote: 'Marking this order as refunded removes its revenue from business, customer and dashboard reports.',
      status: {
        paid: 'Paid',
        unpaid: 'Unpaid',
        refunded: 'Refunded',
      },
      types: {
        bankTransfer: 'Bank Transfer',
        cash: 'Cash',
        other: 'Other',
        placeholder: 'Type payment method',
      },
      proof: {
        label: 'Proof of payment',
        attach: 'Attach file',
        uploading: 'Uploading…',
        accepted: 'PDF, JPG or PNG — max 5 MB',
        remove: 'Remove attachment',
        invalidFormat: 'Invalid file format. Use PDF, JPG or PNG.',
        fileTooLarge: 'File too large. Max 5 MB.',
        uploadFailed: 'Upload failed. Try again.',
        downloadFailed: 'Download failed. Try again.',
      },
      toast: {
        saved: 'Payment updated.',
        failed: 'Failed to update payment.',
      },
    },
    formHeader: {
      customer: 'Customer',
      customerPlaceholder: 'Select or type new customer',
      customerEmail: 'Customer email',
      orderDate: 'Order date',
      terms: 'Terms',
      selectTerms: 'Select terms',
      net7: 'Net 7',
      net14: 'Net 14',
      net30: 'Net 30',
      dueOnReceipt: 'Due on receipt',
      invoiceDate: 'Invoice date',
      dueDate: 'Due date',
      balanceDue: 'BALANCE DUE',
      invoiceNo: 'Invoice no.',
      store: 'Store',
    },
    formDetails: {
      billingAddress: 'Billing address',
      shippingTo: 'Shipping to',
      sameAsBilling: 'Same as billing address',
      expectedShipDate: 'Expected ship date',
      shipVia: 'Ship via',
      shippingDate: 'Shipping date',
      trackingNo: 'Tracking no.',
      sales: 'Sales',
      tags: 'Tags',
      tagsPlaceholder: 'Start typing to add a tag',
    },
    items: {
      items: 'Items',
      clearAllLines: 'Clear all lines',
      addLines: 'Add lines',
      productService: 'Product/Service',
      selectProductService: 'Select Product/Service',
      remaining: 'Remaining',
      rate: 'Rate ({{currencySymbol}})',
      ratePerKg: 'Rate ({{currencySymbol}}/kg)',
      qtyKg: 'Qty (kg)',
      qtyG: 'Qty (g)',
      itemType: 'Item Type',
      unitPriceGreaterThanZero: '*Unit price should be greater than 0',
      subtotal: 'Subtotal',
      shipping: 'Shipping',
      discount: 'Discount',
      balanceDue: 'Balance due',
      errors: {
        quantityExceedsRemainingLimit: 'Quantity exceeds remaining limit',
      },
      lotPicker: {
        label: 'Lot',
        fifoAuto: 'FIFO (auto)',
        fifoHint: 'Drain oldest active lot first',
        manualHeader: 'Pin to a specific lot',
        noProcessType: 'No process type',
        unknownLot: 'Unknown lot',
        noLots: 'No active lots available',
      },
    },
    footer: {
      messageOnInvoice: 'Message on invoice',
      internalNotesPlaceholder: 'Internal notes...',
      preparing: 'Preparing...',
      previewInvoice: 'Preview',
      downloadInvoice: 'Download Invoice',
      updateOrder: 'Update Order',
      createOrder: 'Create Order',
      confirmCancellationTitle: 'Confirm Cancellation',
      confirmCancellationMessage: 'Warning: Cancelling a delivered order will reverse the financial transaction. Are you sure you want to proceed?',
      confirmCancelOrder: 'Yes, Cancel Order',
    },
    list: {
      title: 'Sales Orders',
      loading: 'Loading sales orders...',
      errorLoad: 'Failed to load sales orders',
      newButton: 'New Sales Order',
      filterStatus: 'Filter Status:',
      filters: {
        search: 'Search',
        searchPlaceholder: 'Search order number or customer',
        dates: 'Dates',
        dateRangeFilters: 'Date Range Filters',
        orderDate: 'Order Date',
        applyFilters: 'Apply Filters',
      },
      totalAmount: 'Total Amount',
      markAsSent: 'Mark as Sent',
      markAsDelivered: 'Mark as Delivered',
      empty: 'No sales orders found.',
      status: {
        all: 'All',
        draft: 'Draft',
        sent: 'Sent',
        delivered: 'Delivered',
        cancelled: 'Cancelled',
      },
      table: {
        orderNumber: 'Order #',
        customer: 'Customer',
        date: 'Date',
        total: 'Total',
        deliveryStatus: 'Delivery Status',
        paymentStatus: 'Payment Status',
        status: 'Status',
        internalNotes: 'Internal Notes',
      },
      modal: {
        createTitle: 'Create Sales Order',
        editTitle: 'Edit {{orderNumber}}',
        customerDetails: 'Customer Details',
      },
    },
  },
  selling: {
    customerList: {
      title: 'Customer Master',
      loading: 'Loading customers...',
      empty: 'No customers found.',
      confirmDelete: 'Are you sure?',
      table: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        billingAddress: 'Billing Address',
        shippingAddress: 'Shipping Address',
        messageOnInvoice: 'Message on Invoice',
        internalNotes: 'Internal Notes',
        actions: 'Actions',
      },
      actions: {
        add: 'Add Customer',
        edit: 'Edit',
        delete: 'Delete',
        openWhatsApp: 'Open WhatsApp',
        call: 'Call',
      },
      modal: {
        addTitle: 'Add Customer',
        editTitle: 'Edit Customer',
      },
      toast: {
        created: 'Customer created successfully.',
        updated: 'Customer updated successfully.',
        deleted: 'Customer deleted successfully.',
        saveFailed: 'Failed to save customer.',
        deleteFailed: 'Failed to delete customer.',
        inUse: 'This customer cannot be deleted because it is currently in use.',
      },
    },
    service: {
      customerNotFoundCreateFirst: 'Customer not found. Please create the customer first (Email and Billing Address are required).',
    },
  },
  workOrders: {
    viewOtherWorkOrders: 'View other work orders',
    historyList: {
      title: 'Select a work order',
    },
    common: {
      areYouSure: 'Are you sure?',
      cancelling: 'Cancelling...',
      deleting: 'Deleting...',
    },
    bulk: {
      modeLabel: 'Creation mode',
      modeSingle: 'Single',
      modeBulk: 'Bulk',
      scheduleTitle: 'Schedule {{index}}',
      addSchedule: 'Add New Schedule',
      removeSchedule: 'Remove this schedule',
      maxReached: 'You can create at most {{max}} schedules at a time.',
      draftBadge: 'Draft',
      savedBadge: 'Saved',
      savingDrafts: 'Saving drafts...',
      scheduling: 'Scheduling...',
      fixFieldsFirst: 'Some schedules have missing required fields. Fix them and try again.',
      createFailed: 'Schedule {{index}} failed: {{message}}. {{created}} draft(s) were created — press Save as Draft again to retry the rest.',
      incompleteHint: 'Select a green bean, at least one lot with a weight, and a roast type to schedule this work order.',
      lotBudgetHint: 'Remaining amounts already account for lots taken by the other schedules in this batch.',
      confirmTitle: 'Schedule work orders',
      confirmBody: '{{ready}} schedule(s) will be scheduled. {{pending}} schedule(s) are incomplete and will stay as drafts.',
      confirmPendingList: 'Staying as draft:',
      confirmContinue: 'Continue',
      scheduleResult: '{{scheduled}} work order(s) scheduled, {{draft}} left as draft',
      scheduleFailed: 'Failed to schedule {{code}}: {{message}}',
      missingFieldsBody: 'Fill in the required fields before scheduling. Nothing has been saved yet.',
      issues: {
        bean: 'Green bean',
        lots: 'At least one lot with a weight',
        roastType: 'Roast type',
        insufficientStock: 'Lot weight exceeds remaining stock',
        outputExceedsInput: 'Roast output exceeds roast input',
      },
    },
    page: {
      title: 'Work Orders',
      loading: 'Loading work orders...',
      newWorkOrder: 'New Work Order',
      packingProcess: 'Packing Process',
      mode: 'Mode',
      roasting: 'Roasting',
      packing: 'Packing',
      emptyFiltered: 'No work orders found matching your filters.',
      errorCancel: 'Failed to cancel work order. Please try again.',
      errorDelete: 'Failed to delete work order. Please try again.',
      viewWorkOrder: 'View Work Order',
      updateWorkOrder: 'Update Work Order',
      moveToInventory: 'Move to Inventory (Roasted Goods)',
      errors: {
        loadDetailsFailed: 'Failed to load work order details',
        selectAtLeastOneLot: 'Cannot move work order to scheduled/completed. Please select at least one lot.',
        updateFailed: 'Failed to update work order',
        completeFailed: 'Failed to complete work order',
        onlyCancelledDelete: 'Only cancelled work orders can be deleted.',
        saveFinishedGoodFailed: 'Failed to save finished good',
      },
      success: {
        roastedBulkUpdated: 'Roasted Bulk updated successfully!',
        roastedBulkCreated: 'Roasted Bulk created successfully!',
      },
    },
    filters: {
      search: 'Search',
      searchPlaceholder: 'Search by work order number, recipe name, item name, item code...',
      status: 'Status',
      roastType: 'Roast Type',
      machine: 'Machine',
      machineType: 'Machine Type',
      allStatuses: 'All Statuses',
      allRoastTypes: 'All Roast Types',
      allMachines: 'All Machines',
      allMachineTypes: 'All Machine Types',
      toggleFilters: 'Filters',
      activeCount: '{{count}} active',
      selectedCount: '{{count}} selected',
    },
    table: {
      workOrder: 'Work Order',
      item: 'Item',
      roastType: 'Roast Type',
      roastInput: 'Roast Input (kg)',
      roastOutput: 'Roast Output (kg)',
      shrinkage: 'Shrinkage',
      orderDate: 'Order Date',
      actualEnd: 'Actual End',
      status: 'Status',
      actions: 'Actions',
    },
    cards: {
      input: 'Input',
      output: 'Output',
      order: 'Order',
      target: 'Target',
      na: 'N/A',
    },
    actions: {
      start: 'Start',
      complete: 'Complete',
      cancel: 'Cancel',
      delete: 'Delete',
      stockIn: 'Stock In',
      updateStock: 'Update Stock',
      editStock: 'Edit Stock',
      editFinishedGoods: `Edit ${PROTECTED_TECHNICAL_TERMS.finishedGood}`,
      createFinishedGoods: `Create ${PROTECTED_TECHNICAL_TERMS.finishedGood}`,
      downloadProfile: 'Download Profile',
      saveRecipe: 'Save Recipe',
      savingDraft: 'Saving Draft...',
      saveAsDraft: 'Save as Draft',
      saveAsScheduled: 'Save as Scheduled',
      saveAsComplete: 'Save as Complete',
      stopwatchInProgressHint: 'Finish the stopwatch (or reset it) before saving as scheduled or complete.',
      onboardingOutputRequiredHint: 'Enter the roast output weight to finish this practice roast — the tour only continues once the work order is completed.',
    },
    status: {
      draft: 'Draft',
      scheduled: 'Scheduled',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    fgProductions: {
      title: 'Production Drafts & Runs',
      subtitle: 'Create drafts, complete packing process, and review history.',
      searchPlaceholder: 'Search packing code, item name, item code',
      loading: 'Loading productions...',
      empty: 'No production records found matching your filters.',
      errorLoad: 'Failed to load finished goods productions. Please try again.',
      table: {
        packingCode: 'Packing Code',
        finishedGood: PROTECTED_TECHNICAL_TERMS.finishedGood,
        qty: 'Qty',
        status: 'Status',
        created: 'Created',
        actions: 'Actions',
      },
      actions: {
        complete: 'Complete',
        view: 'View',
        cancel: 'Cancel',
      },
      status: {
        all: 'All Status',
        draft: 'Draft',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
      cancelModal: {
        title: 'Cancel Production Draft',
        description: 'This will cancel the draft and you may lose the unfinished production data.',
        errorCancel: 'Failed to cancel production draft. Please try again.',
        confirm: 'Yes, cancel draft',
      },
    },
    waste: {
      title: `${PROTECTED_TECHNICAL_TERMS.roastedBulk} Waste Management`,
      processing: 'Processing...',
      empty: 'No components to process.',
      manualLabel: 'Manual',
      manualAdjustmentNote: 'Manual remaining adjustment (keep): {{from}} -> {{to}} kg',
      wasteNote: 'Waste roasted bulk after finished goods production',
      errorNoLot: 'No lot found for {{itemName}} ({{roastType}})',
      errorStockChanged: 'Remaining stock changed. Please reopen waste management for {{itemName}}.',
      errorProcess: 'Failed to process waste management. Please try again.',
      table: {
        item: 'Item',
        remainingKg: 'Remaining (kg)',
        action: 'Action',
      },
      actions: {
        keep: 'Keep',
        waste: 'Waste',
        confirm: 'Confirm',
        confirmAdjust: 'Confirm & Adjust',
      },
      confirmManualTitle: 'Confirm Manual Remaining',
      confirmManualHeading: 'There are remaining values that were manually adjusted',
      confirmManualDescription:
        'The items below are marked as keep, but their remaining values were manually modified. This will result in stock adjustments based on the manual values.',
    },
    cancelModal: {
      title: 'Cancel Work Order',
      scheduledDescription:
        'Cancelling this scheduled work order will restore roast input stock and write reversal stock movements.',
      defaultDescription: 'Cancelling this work order will mark it as cancelled and stop further processing.',
      confirm: 'Yes, cancel work order',
    },
    deleteModal: {
      title: 'Delete Cancelled Work Order',
      description: 'This will permanently delete the cancelled work order and cannot be undone.',
      confirm: 'Yes, delete work order',
    },
    closeEditConfirmModal: {
      title: 'Close Work Order Form',
      description: 'Any unsaved changes to this work order will be lost. Do you want to continue?',
      keepEditing: 'Keep editing',
      discard: 'Discard changes',
    },
    roastTypeEdit: {
      saveButton: 'Save Roast Type',
      warningTitle: 'Edit Roast Type on Completed Work Order',
      warningMessage:
        'You are about to edit the roast type of a completed work order. Please review before confirming.',
      scopeHeader: 'Scope of this change',
      scopeOnlyRoastType: 'Only the roast type will be updated (header, snapshot, and any saved recipe).',
      scopeStockUnchanged: 'Stock that has already been deducted from inventory will NOT be reverted.',
      backToEdit: 'Back to edit',
      confirm: 'Yes, update roast type',
      saving: 'Updating...',
      savedToast: 'Roast type updated',
      saveFailed: 'Failed to update roast type.',
    },
    completeSummaryModal: {
      title: 'Confirm Complete Roast',
      banner: 'Review before finalizing',
      headline: 'Ready to complete this roast?',
      notice: 'Once completed, stock will be deducted and this work order will be locked.',
      section01: 'Beans & Lots',
      section02: 'Input → Output',
      section03: 'Agtron Meter',
      section04: 'Roast Type',
      section05: 'Expiry Date',
      noBean: 'No bean selected',
      noLots: 'No lots recorded',
      total: 'Total used',
      input: 'Input',
      output: 'Output',
      shrinkage: 'Shrinkage',
      wholeBean: 'Whole',
      groundBean: 'Ground',
      noAgtron: 'Not recorded',
      noRoastType: 'Not specified',
      expiryOptional: 'Optional',
      expiryClear: 'Clear',
      expiryHelp: 'Applied to the roasted output stock created from this work order.',
      confirm: 'Confirm & Complete',
      confirming: 'Completing...',
      cancel: 'Back to edit',
    },
    beanSelection: {
      title: 'Bean Selection',
      selectGreenBean: 'Select a green bean...',
      autoCalculate: 'Auto Calculate',
      calculating: 'Calculating...',
      optional: '(optional)',
      selectRoastType: 'Select roast type',
      noLotsTitle: 'This green bean has no available lots',
      noLotsHint: 'Receive stock through a Purchase Order before scheduling a roast for this bean.',
      roast: {
        sample: 'Sample Roast',
        filter: 'Filter Roast',
        omni: 'Omni Roast',
        espresso: 'Espresso Roast',
      },
    },
    roastProfile: {
      title: 'Roast Profile',
      useRoastProfile: 'Use Roast Profile',
      useStopwatchMode: 'Use Stopwatch Mode',
      roastingReference: 'Roasting Reference',
      artisanProfile: 'Artisan Profile',
      useArtisanProfile: 'Use Artisan Profile',
      linkArtisanProfile: 'Link Artisan Profile',
      noDate: 'No date',
      untitled: 'Untitled',
      selectUnsyncedProfile: 'Select unsynced profile...',
      clearArtisanProfile: 'Clear artisan profile',
      roastProfiles: 'Roast Profiles',
      noType: 'No type',
      selectProfile: 'Select a profile...',
      selectGreenBeanFirst: 'Select a green bean first...',
      newProfile: 'New Profile',
      saveAsNewProfile: 'Save as a new profile',
      snapshotProfile: 'Snapshot Profile',
    },
    modals: {
      totalUsedWeightMismatch: 'Total Used Weight Mismatch',
      totalUsedWeightMessage: 'Total used weight from lots different with Roast Input.',
      totalUsedWeight: 'Total Used Weight',
      adjustInputOrLots: 'Adjust Roast Input or quantity per lot to match before proceeding.',
      roastOutputExceedsInput: 'Roast Output Exceeds Input',
      outputCannotExceedInput: 'Roast Output cannot be greater than Roast Input.',
      adjustOutput: 'Adjust Roast Output to match before proceeding.',
      warning: 'Warning',
      back: 'Back',
      ok: 'OK',
    },
    form: {
      loading: 'Loading...',
      saved: 'Saved ✓',
      workOrderCode: 'Work Order Code',
      selectOrTypeMachine: 'Select or type machine',
      selectOrTypeRoaster: 'Select or type roaster',
      minInput: 'Min Input',
      maxInput: 'Max Input',
      addMachineType: 'Add Machine Type',
      addRoaster: 'Add Roaster',
      unknownLot: 'Unknown Lot',
      used: 'Used',
      unknownRecipe: 'Unknown Recipe',
      unlinkModal: {
        title: 'Unlink Artisan Profile',
        message: 'Unlink Artisan Profile from this Work Order? The Artisan roast profile relationship on the Work Order will be released.',
        no: 'No',
        unlinking: 'Unlinking...',
        yesUnlink: 'Unlink',
      },
      toast: {
        machineCreated: 'Machine created successfully.',
        machineCreateFailed: 'Failed to create machine.',
        roasterCreated: 'Roaster created successfully.',
        roasterCreateFailed: 'Failed to create roaster.',
        machineTypeCreated: 'Machine type created successfully.',
        artisanUnlinked: 'Artisan profile unlinked successfully.',
        artisanUnlinkFailed: 'Failed to unlink artisan profile.',
      },
      errors: {
        outputExceedsInput: 'Roast output cannot be greater than roast input.',
        loadArtisanProfileDetailsFailed: 'Failed to load Artisan profile details.',
        autoCalculateLotsFailed: 'Failed to auto-calculate lots',
        allocationErrorOccurred: 'An error occurred during allocation.',
        roastTypeRequiredWhenUsingProfile: 'Roast type is required when using a roast profile.',
        roastTypeRequired: 'Roast type is required.',
        selectAtLeastOneLotBeforeScheduling: 'Please select at least one lot before scheduling this work order.',
        selectAtLeastOneLotWithQuantityIfBeanSelected: 'Please select at least one lot with quantity if a bean is selected.',
        lotsExceedRemainingStock: 'One or more lots exceed their remaining stock. Please adjust the quantities.',
        createFailedCheckInputs: 'Failed to create work order. Please check inputs.',
      },
    },
  },
  inventory: {
    import: {
      buttonLabel: 'Import Green Bean Inventory',
      modalTitle: 'Import Green Bean Inventory',
      step1Title: 'Upload',
      step2Title: 'Review & Edit',
      step3Title: 'Done',
      downloadTemplate: 'Download template',
      uploadHint: 'Drop your .xlsx file here or click to browse',
      uploadParsing: 'Parsing…',
      reviewSummary: '{{rows}} rows · {{errors}} errors',
      submit: 'Import',
      cancel: 'Cancel',
      back: 'Back',
      done: 'OK',
      successToast: '{{count}} green bean rows imported',
      errors: {
        notXlsx: 'File must be an .xlsx spreadsheet',
        tooLarge: 'File is too large (max 5 MB)',
        tooManyRows: 'Too many rows (max 500)',
        missingHeader: 'Spreadsheet is missing a required column',
        serverError: 'Import failed — please try again',
      },
    },
    importRoastedBulk: {
      buttonLabel: 'Import Roasted Bulk',
      modalTitle: 'Import Roasted Bulk',
      step1Title: 'Pick green beans',
      step2Title: 'Upload',
      step3Title: 'Review',
      step4Title: 'Done',
      errors: {
        serverError: 'Import failed — please check your rows and try again',
      },
    },
    modal: {
      titleCreate: 'Create New Item',
      titleEdit: 'Edit Item',
      errorSave: 'Failed to save item: {{message}}',
      errorFixFields: 'Please fix the highlighted fields and try again.',
      errorSummaryTitle: 'Resolve {{count}} issues before saving',
      errorSummaryTitleOne: 'Resolve 1 issue before saving',
      errorSummarySubtitle: 'Click any item below to jump to the field that needs attention.',
      sections: {
        basicInformation: 'Basic Information',
        greenBeanExtrinsic: `${PROTECTED_TECHNICAL_TERMS.greenBean} Extrinsic`,
        stockCost: 'Stock & Cost',
        sellingPackaging: 'Selling & Packaging',
      },
      fields: {
        code: 'Code',
        codeAuto: 'Auto-generated',
        name: 'Name',
        status: 'Status',
        description: 'Description',
        itemType: 'Item Type',
        location: 'Location',
        locationPlaceholder: 'e.g. Warehouse 1',
        producer: 'Name of Producer',
        producerPlaceholder: 'Select or add producer',
        species: 'Species',
        speciesPlaceholder: 'e.g. Arabica',
        variety: 'Variety',
        varietyPlaceholder: 'e.g. Bourbon',
        processType: 'Process Type',
        processTypePlaceholder: 'Specify process type',
        quantity: 'Quantity',
        initialQuantity: 'Initial Quantity',
        standardCost: 'Standard Cost',
        reorderWarning: 'Reorder Warning',
        reorderQuantity: 'Reorder Quantity',
        estimatedSellingPrice: 'Estimated Selling Price',
        packSize: 'Pack Size',
        packSizePlaceholder: 'e.g. 0.5',
      },
      loadingItemDetails: 'Loading item details...',
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
      validation: {
        max100: 'Max 100 characters',
      },
      values: {
        na: 'N/A',
        unknownItem: 'Unknown Item',
      },
      actions: {
        saving: 'Saving...',
        creating: 'Creating...',
        saveChanges: 'Save Changes',
        createItem: 'Create Item',
        close: 'Close',
      },
      warning: {
        title: 'Warning',
        inactiveBlocked: `Cannot set status to inactive because this ${PROTECTED_TECHNICAL_TERMS.greenBean} still has active lots.`,
        ok: 'OK',
      },
    },
    lotDetail: {
      loading: 'Loading lot details...',
      notFound: 'Lot not found',
      uploading: 'Uploading...',
      loadingShort: 'Loading...',
      titleWithNumber: 'Lot {{lotNumber}}',
      uploadHint: 'PNG, JPG, or WEBP up to 2MB',
      emptyMovements: 'No movements found.',
      status: {
        active: 'Active',
        depleted: 'Depleted',
      },
      sections: {
        lotDetails: 'Lot Details',
        inventoryStatus: 'Inventory Status',
        stockMovementHistory: 'Stock Movement History',
        profiles: 'Profiles',
      },
      fields: {
        item: 'Item',
        receivedDate: 'Received Date',
        expiryDate: 'Expiry Date',
        source: 'Source',
        linkedSample: 'Linked Sample',
        supplier: 'Supplier',
        unitPrice: 'Unit Price',
        initialQuantity: 'Initial Quantity',
        remainingQuantity: 'Remaining Quantity',
        greenBeanPhoto: 'Green Bean Photo',
        photoUploadedAt: 'Uploaded At',
        quantityChange: 'Quantity Change',
        user: 'User',
        photo: 'Photo',
      },
      values: {
        na: 'N/A',
        unknownItem: 'Unknown Item',
      },
      actions: {
        viewSample: 'View Sample',
        changePhoto: 'Change Photo',
        clickUpload: 'Click to upload photo',
        removePhoto: 'Remove photo',
      },
      errors: {
        uploadFailed: 'Failed to upload image',
        fileTooLarge: 'File size must be less than 2MB',
        invalidFileType: 'Only PNG, JPG, or WEBP are allowed',
      },
    },
    roastedBulk: {
      loadingLots: 'Loading roasted lots...',
      emptyLots: 'No roasted lots found for this group.',
      empty: 'No roasted lots found matching your filters.',
      woPrefix: 'WO',
      remaining: 'Remaining',
      in: 'In',
      out: 'Out',
      shrink: 'Shrink',
      expiry: 'Expiry',
      history: 'History',
      totalStockKg: 'Total Stock (kg)',
      averagePrice: 'Average Price',
      unknownRoastType: 'Unknown Roast Type',
      actions: 'Actions',
      adjust: {
        button: 'Reduce Stock',
        title: 'Reduce Stock',
        subtitle: 'Stock is drawn from the oldest lots first (FIFO).',
        availableLabel: 'Available',
        afterLabel: 'After this reduction',
        quantityLabel: 'Quantity',
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Select a reason...',
        reasons: {
          waste: 'Waste / Spoilage',
          sample: 'Sample / Cupping',
          internal: 'Internal Use',
          correction: 'Stock Correction',
        },
        notesLabel: 'Notes',
        notesPlaceholder: 'What happened?',
        previewTitle: 'Lots to be drawn',
        previewEmpty: 'Enter a quantity to see which lots will be used.',
        previewTotal: 'Total cost',
        columns: {
          workOrder: 'Work Order',
          quantity: 'Drawn',
          unitCost: 'Cost / kg',
          cost: 'Cost',
        },
        lotEmptied: 'Lot emptied',
        submit: 'Reduce Stock',
        submitting: 'Reducing...',
        cancel: 'Cancel',
        errors: {
          quantityRequired: 'Enter a quantity greater than zero.',
          quantityTooLarge: 'Only {{available}} is available in this group.',
          reasonRequired: 'Select a reason.',
          notesRequired: 'Notes are required.',
          failed: 'Failed to reduce stock.',
        },
        success: 'Reduced {{quantity}} ({{cost}}) from {{item}}.',
      },
      columns: {
        workOrder: 'Work Order',
        inputKg: 'Input (kg)',
        outputKg: 'Output (kg)',
        shrinkage: 'Shrinkage',
        remaining: 'Remaining',
        expiry: 'Expiry',
        cogs: 'Cost of goods sold',
        beanProfile: 'Bean Profile',
        roastProfile: 'Roast Profile',
        cuppingProfile: 'Cupping Profile',
      },
    },
    finishedGoods: {
      componentsTitle: `${PROTECTED_TECHNICAL_TERMS.roastedBulk} Components`,
      empty: 'No components added. Use "Add Component" to start.',
      emptyInventory: 'No inventory items found matching your filters.',
      summary: {
        title: 'Production Summary',
        detailCalculation: 'Detail Calculation',
        totalInput: 'Total Input',
        weightPerPcs: 'Weight / Pcs',
        miscellaneousCost: 'Miscellaneous Cost',
        packaging: 'Packaging',
        estimatedSellingPricePerPcs: 'Estimated Selling Price / Pcs',
        currencyPlaceholder: 'Rp 0',
        unitPlaceholder: '1',
        totalCogs: 'Total COGS',
        unitCostPerPcs: 'Unit Cost / Pcs',
      },
      placeholders: {
        selectRoastedBulk: 'Select roasted bulk...',
      },
      actions: {
        add: 'Add',
        remove: 'Remove',
        addComponent: 'Add Component',
        removeComponent: 'Remove Component',
      },
      columns: {
        roastedBulkItem: 'Roasted Bulk Item',
        roastType: 'Roast Type',
        availableKg: 'Available (kg)',
        allocation: 'Allocation',
        finalAllocation: 'Final Allocation',
      },
      errors: {
        allocationExceeds: 'Allocation exceeds pack size',
        totalAllocationPrefix: 'Total allocation',
        totalAllocationSuffix: 'must not exceed pack size',
        totalInputVsTarget: 'Total Input vs Target',
        totalInputWeightPrefix: 'Total input weight',
        totalInputWeightSuffix: 'does not match target',
        stockExceeded: 'STOCK EXCEEDED',
      },
      finalProduct: {
        title: 'Final Product',
        packSize: 'Pack Size',
        selectFinishedGoods: 'Select finished goods...',
        enterNewName: 'Enter new finished goods name',
        packSizePlaceholder: '0.500',
        required: 'Final product is required.',
        packSizeRequired: 'Pack size is required.',
        nameAlreadyExists: 'A finished good with this name already exists.',
      },
    },
    sampleModal: {
      title: 'Add Sample',
      errors: {
        dateRequired: 'Date is required',
        createFailed: 'Failed to create sample',
      },
    },
    samples: {
      empty: 'No active samples found.',
      actions: {
        addSample: 'Add Sample',
      },
    sampleQA: {
      notChecked: 'Not Checked',
      passed: 'Passed',
      failed: 'Failed',
    },
    },
    basicInfo: {
      title: 'Basic Information',
      generatedBySystem: 'Generated by system',
      unitOfMeasure: 'Unit of Measure',
      units: {
        kg: 'kg',
        g: 'g',
        lb: 'lb',
        oz: 'oz',
        pcs: 'pcs',
        bag: 'bag',
      },
    },
    createItem: {
      cogsBatch: 'Cost of Goods Sold Batch ({{currencySymbol}})',
      roastInputFromWorkOrder: 'Roast Input from Work Order',
      roastOutput: 'Roast Output',
      updatesHistory: '(Updates History)',
      quantityPlaceholder: 'e.g. 5.2',
      workOrderIdOptional: 'Work Order ID (Optional)',
      expiryDateOptional: 'Expiry Date (Optional)',
      create: 'Create',
      update: 'Update',
      errors: {
        max100Chars: 'Max 100 characters',
        outputGreaterThanZero: 'Roast output must be greater than 0',
        outputExceedsInput: 'Roast output cannot exceed of roast input',
      },
      shrinkageModal: {
        title: 'High Shrinkage Warning',
        warningPrefix: 'Warning: Shrinkage is',
        comparisonDetails: 'Comparison Details:',
        input: 'Input',
        output: 'Output',
        confirm: 'Shrinkage above 25%. Is the input and output data correct?',
      },
    },
    createPage: {
      profileCode: 'Profile Code',
      profileName: 'Profile Name',
    },
    page: {
      confirmDeleteItem: 'Are you sure you want to delete this item?',
      failedDeleteItemPrefix: 'Failed to delete item: ',
    },
    filters: {
      searchNameOrCode: 'Search by name or code...',
    },
    header: {
      addNewGreenBeanItem: 'Add New Green Bean Item',
    },
    typeFilter: {
      itemType: 'Item Type',
      greenBeans: 'Green Beans',
      roastedBulks: 'Roasted Bulks',
      finishedGoods: 'Finished Goods',
    },
    modals: {
      confirmUpdateData: 'Are you sure want to update the data?',
      selectTasteProfile: 'Select Taste Profile',
      itemHasTasteProfiles: 'This item has {{count}} taste profile. Select one to view:',
      finishedGoodsTasteProfile: 'Finished Goods Taste Profile',
      loadTasteProfilesFailed: 'Failed to load taste profiles',
      tasteProfileTitlePrefix: 'Taste Profile',
      roastProfileTitlePrefix: 'Roast Profile',
    },
    roastProfileModal: {
      errors: {
        loadFailed: 'Failed to load roast profile',
        saveFailed: 'Failed to save roast profile',
      },
    },
    finishedGoodsTaste: {
      full: 'Full',
      viewOtherProfiles: 'View Other Profiles',
      notesPlaceholder: 'Optional additional notes about this finished product taste profile',
      saveTasteProfile: 'Save Taste Profile',
      errors: {
        loadExistingFailed: 'Failed to load existing taste profile',
        saveFailed: 'Failed to save taste profile. Please try again.',
        selectFlavorNote: 'Select at least one flavor note.',
      },
    },
    history: {
      title: 'Inventory History',
      forLot: 'for Lot: {{lotNumber}}',
      forRoasted: 'Inventory History for Roasted: {{workOrderNumber}}',
      forFinishedGood: 'Inventory History for FG: {{name}}',
      itemType: 'Item Type',
      currentStock: 'Current Stock',
      cannotAdjustInvalidType: 'Cannot adjust stock because the item type is invalid or missing.',
      manualStockAdjustment: 'Manual Stock Adjustment',
      selectActiveLot: 'Select Active Lot',
      selectLotOption: '-- Select Lot --',
      noActiveLots: 'No active lots available for adjustment.',
      action: 'Action',
      actionAdd: '➕ Add Stock',
      actionRemove: '➖ Remove Stock',
      quantity: 'Quantity',
      unitKg: 'kg',
      placeholderKg: '0.000',
      placeholderGram: '0',
      reasonNotes: 'Reason / Notes',
      reasonPlaceholder: 'e.g. Damage check, restock...',
      updateStock: 'Update Stock',
      empty: 'No history found for this item.',
      lotNumber: 'Lot Number',
      quantityUnit: 'Quantity ({{unit}})',
      warehouse: 'Warehouse',
      user: 'User',
      errors: {
        unsupportedItemType: 'Unsupported item type "{{itemType}}".',
        loadFailed: 'Failed to load inventory history.',
        itemIdRequired: 'Inventory item id is required.',
        selectLotRequired: 'Please select a lot for adjustment.',
        validQuantityRequired: 'Please enter a valid quantity.',
        exceedsAvailable: 'Only {{available}} {{unit}} available to remove.',
        adjustFailed: 'Failed to adjust stock. Please try again.',
      },
    },
    lots: {
      remainingQty: 'Remaining Qty',
      viewHistoryAdjustStock: 'View History & Adjust Stock',
    },
    table: {
      columns: {
        type: 'Type',
        stock: 'Stock',
        averagePrice: 'Average Price',
        costOfGoodsSold: 'Cost of Goods Sold',
        estimatedSellingPrice: 'Estimated Selling Price',
        stockStatus: 'Stock Status',
        unitCost: 'Unit Cost',
        estimatedPrice: 'Estimated Price',
      },
      status: {
        available: 'Available',
        depleted: 'Depleted',
        low: 'Low Inventory',
      },
      actions: {
        clickViewHistory: 'Click to view history',
        add: 'Add',
        restock: 'Re-stock',
      },
    },
    stockCost: {
      title: 'Stock & Cost',
      reorderWarning: 'Reorder Warning',
      reorderQuantity: 'Reorder Quantity',
    },
    mobile: {
      actions: {
        roast: 'Roast',
        bean: 'Bean',
        taste: 'Taste',
      },
    },
    ingredients: {
      empty: 'No ingredients found.',
      itemId: 'Item ID',
    },
    materialHistory: {
      title: 'Material History (Production Inputs)',
      notFound: 'No material history found (Work Order ID: {{workOrderId}}).',
      noIngredients: 'No ingredients recorded for this Work Order.',
      quantityUsed: 'Quantity Used',
      lots: 'Lots',
    },
  },
  artisanSync: {
    bulk: {
      title: 'Bulk Upload',
      uploading: 'Uploading {{done}} of {{total}}...',
      fileName: 'File',
      status: 'Status',
      detail: 'Detail',
      created: 'Created',
      duplicate: 'Already uploaded',
      invalid: 'Not a valid roast file',
      failed: 'Failed',
      fileTooLarge: 'File is too large to upload',
      chunkFailed: 'Upload failed - try these files again',
      summary: '{{created}} created, {{duplicate}} already existed, {{failed}} failed',
      viewProfile: 'View',
      done: 'Done',
    },
    delete: {
      action: 'Delete',
      deleting: 'Deleting...',
      selected: 'Delete selected ({{count}})',
      selectedCount: '{{count}} selected',
      selectAll: 'Select all deletable profiles',
      title: 'Delete roast profile?',
      message: '{{count}} roast profile(s) will be permanently removed. This cannot be undone.',
      syncedTooltip: 'Synced to a work order - unlink it there first',
      blocked: 'Linked to work order {{number}}. Unlink it there first.',
      someBlocked: '{{count}} profile(s) were skipped because they are linked to a work order.',
      failed: 'Failed to delete roast profile',
    },
    file: {
      download: 'Download .alog',
      downloading: 'Downloading...',
      available: 'Original file available',
      notStored: 'Original file not saved',
      notStoredBatch: 'Some profiles were created without their original file - file storage is unavailable.',
    },
    values: {
      unknown: 'Unknown',
      unknownOperator: 'Unknown Operator',
      untitledRoast: 'Untitled Roast',
      lightRoast: 'Light',
      workOrder: 'Work Order',
    },
    errors: {
      saveProfile: 'Failed to save profile to database',
      syncProfile: 'Failed to sync profile to work order',
      unsupportedFile: 'Unsupported file type. Please upload a .json or .alog file exported from Artisan.',
      invalidFileFields: 'Invalid Artisan file: missing required fields (timex, temp1, temp2).',
      parseFile: 'Failed to parse file: {{message}}',
      duplicateFile: 'This file has already been uploaded.',
      unknown: 'Unknown error',
    },
    page: {
      title: 'Artisan Profiles',
      subtitle: 'Manage and sync roast data exported from Artisan Roaster Software',
      uploadNewFile: 'Upload New File',
    },
    filters: {
      allStatus: 'All Status',
      syncedToWo: 'Synced to WO',
      synced: 'Synced',
      unsynced: 'Unsynced',
      searchRoaster: 'Search Roaster...',
    },
    table: {
      dateTime: 'Date & Time',
      roastTitle: 'Roast Title',
      roaster: 'Roaster',
      machine: 'Machine',
      status: 'Status',
      action: 'Action',
      actionsSr: 'Actions',
    },
    actions: {
      view: 'View',
    },
    loadingProfiles: 'Loading profiles...',
    emptyProfiles: 'No Artisan profiles found.',
    upload: {
      title: 'Upload Artisan Profile',
      subtitle: 'Parse and persist Artisan .json or .alog files',
      errorTitle: 'Upload Error',
      dropHere: 'Drag & drop your Artisan files here',
      supportedFormats: 'Supports .json and .alog formats',
      bulkHint: 'Select more than one file to upload them in bulk',
      browseFiles: 'Browse Files',
      saving: 'Saving...',
      saveToDb: 'Save to Database',
      profilePreview: 'Profile Preview',
      profilePreviewHint: 'Check the values before saving. Anything you change is marked, and you can reset a single cell, a column, or the whole profile.',
    },
    stage: {
      addFiles: 'Add files',
      removeFile: 'Remove',
      discardAll: 'Discard all',
      saveAll: 'Save {{count}} roasts',
      reading: 'Reading {{done}} of {{total}} files...',
      uploadMore: 'Upload more files',
      resultsTitle: 'Batch saved',
      invalidHelp: 'Remove it and save the rest, or re-export it from Artisan.',
      reason: {
        unsupported: 'Only .json and .alog exports from Artisan can be read.',
        tooLarge: 'This file is larger than the 1.5 MB upload limit.',
        invalidFields: 'The roast data is missing - no timex, temp1 or temp2 in the file.',
        parse: 'The file could not be read as JSON.',
        alreadyStaged: 'This roast is already in the batch.',
      },
      status: {
        ready: 'Ready to save',
        invalid: 'Not saved',
        saving: 'Saving',
        saved: 'Saved',
        duplicate: 'Already uploaded',
        failed: 'Failed',
      },
    },
    meta: {
      operator: 'Operator',
      machine: 'Machine',
      beans: 'Beans',
      drumSpeed: 'Drum Speed',
    },
    detail: {
      loadingTitle: 'Loading Profile',
      loadingBody: 'Loading profile full details...',
      syncedToWo: 'Synced to Work Order: {{workOrderNumber}}',
      unsyncedProfile: 'Unsynced Profile',
      syncToWo: 'Sync to Work Order',
      at: 'at',
      roastChart: 'Roast Chart & Telemetry',
    },
    syncModal: {
      title: 'Sync to Work Order',
      description: 'Select a work order that scheduled and does not use a roast profile.',
      loadingWo: 'Loading work orders...',
      emptyWo: 'No eligible work orders found.',
      syncing: 'Syncing...',
      syncNow: 'Sync Now',
    },
  },
  aiAssistant: {
    image: {
      attach: 'Attach image',
      badType: 'Only PNG, JPEG, or WEBP',
      tooLarge: 'Max 2MB',
      uploadFailed: 'Upload failed',
      notSupported: "Image chat isn't supported on the current model.",
    },
    messages: {
      notConnected: 'AI Assistant is not connected.',
      connectApiKey: 'Connect an API key',
      notConnectedSuffix: 'in Settings to access your data.',
      hiThere: "I'm Qora, your AI Assistant.",
      whereToStart: 'Where should we start?',
      savedAutomatically: 'Your conversations are saved automatically. Ask anything to get started.',
      greetings: {
        morning: 'Good Morning! ☀️',
        afternoon: 'Good Afternoon! 🌤️',
        evening: 'Good Evening! 🌅',
        night: 'Good Night! 🌙',
      },
      suggestions: {
        s1: 'What are we working on today?',
        s2: 'What do you want to get done right now?',
        s3: 'What’s the plan for today?',
        s4: 'What do you need help with today?',
        s5: 'What are you trying to figure out?',
        s6: 'What’s on your plate right now?',
        s7: 'What are you tackling today?',
        s8: 'What do you want to move forward today?',
        s9: 'What are we solving together?',
        s10: 'What needs your attention right now?',
        s11: 'What are you trying to make better?',
        s12: 'What are you focused on today?',
        s13: 'What’s the next thing you want to fix or improve?',
        s14: 'What are you working through right now?',
        s15: 'What’s something you want to sort out?',
        s16: 'What do you want to get clarity on?',
        s17: 'What are you trying to move forward?',
        s18: 'What’s worth tackling right now?',
        s19: 'What do you want to make progress on?',
        s20: 'What’s the one thing you want to handle today?',
      },
      inputPlaceholder: 'Enter a prompt for Qora...',
      send: 'Send',
      cannotProcess: "I'm sorry, I couldn't process that request.",
      backendError: "I'm sorry, I encountered an error. Please ensure the backend is running.",
      callingToolsPrefix: 'Calling tools',
    },
    loading: {
      progressAria: 'Assistant response progress',
      findingTool: {
        title: 'Finding tool',
        subtitle: 'Finding the best way to help...',
      },
      processingRequest: {
        title: 'Processing request',
        subtitle: 'Reviewing your request...',
        shortSubtitle: 'Gathering details...',
      },
      summarizingResponse: {
        title: 'Summarizing response',
        subtitle: 'Putting everything together...',
      },
      preparingAnswer: {
        title: 'Preparing answer',
        subtitle: 'Preparing your answer...',
      },
      longWait: {
        stillWorking: 'Still working on this for you...',
        longerThanUsual: 'Thanks for waiting, this is taking a bit longer than usual.',
        timeout: 'This is taking longer than expected.',
      },
      timeout: {
        message: 'You can wait a little longer or try again.',
        keepWaiting: 'Keep waiting',
        retry: 'Retry',
      },
    },
    chips: {
      createPurchaseOrder: 'Create purchase order',
      createSalesOrder: 'Create sales order',
      createInventory: 'Create inventory',
      createSupplier: 'Create supplier',
      createCustomer: 'Create customer',
      createWorkOrderDraft: 'Create work order draft',
    },
    quickActions: {
      title: 'Quick Actions',
      createPurchaseOrderAi: 'Create Purchase Order AI',
      createSalesOrderAi: 'Create Sales Order AI',
      createInventory: 'Create Inventory',
      createSupplier: 'Create Supplier',
      createCustomer: 'Create Customer',
      createWorkOrderDraft: 'Create Work Order Draft',
    },
    prompts: {
      createPurchaseOrder: 'Create purchase order for ',
      createSalesOrder: 'Create sales order for ',
      createInventory:
        'I want to create inventory item\n\nItem Name (required):\nDescription:\nLocation:\nInitial Quantity:\nStandard Cost:\nReorder Point (kg):\nReorder Quantity (kg):',
      createSupplier:
        'I want to create supplier\n\nBusiness Supplier Name (required):\nContact Person:\nPhone Number:\nEmail:\nInternal Notes:',
      createCustomer:
        'I want to create customer\n\nCustomer Name (required):\nEmail (required):\nPhone Number:\nBilling Address (required):\nShipping Address:\nMessage on Invoice:\nInternal Notes:',
      createWorkOrderDraft:
        'I want to create work order draft\n\nRoast Name:\nMachine:\nRoaster:\nDate:\nTime:\nInternal Notes:',
    },
  },
  reports: {
    header: {
      title: 'General Report',
      exportPdf: 'Export PDF',
      exportExcel: 'Export Excel',
      exporting: 'Exporting...',
    },
    modes: {
      roasting: {
        label: 'Roasting Report',
        description: 'Roasting process, batches, and weight transformation',
        descriptionHeader: 'Detailed roasting batches, bean transformation, and shrinkage insights.',
      },
      greenBeans: {
        label: 'Green Beans Report',
        description: 'Remaining green bean stock and supplier insights',
        descriptionHeader: 'Remaining green bean stock with supplier and valuation details.',
      },
      roastedBeans: {
        label: 'Roasted Bulk Report',
        description: 'Remaining roasted bean stock and production references',
        descriptionHeader: 'Remaining roasted bean inventory sorted by highest quantity.',
      },
      finishedGoods: {
        label: 'Finished Good Report',
        description: 'Remaining finished good stock and production references',
        descriptionHeader: 'Remaining finished good inventory sorted by highest quantity.',
      },
      inventory: {
        label: 'Inventory Report',
        description: 'Green bean, roasted bulk, and finished good stock',
        descriptionHeader: 'Inventory stock across green bean, roasted bulk, and finished good.',
      },
      business: {
        label: 'Business Report',
        description: 'Revenue, customer contributions, and order trends',
        descriptionHeader: 'Revenue summary, customer breakdown, and purchase history trend.',
      },
      customer: {
        label: 'Customer Report',
        description: 'Per-customer purchase history and product mix',
        descriptionHeader: 'Per-customer daily finished-good purchases and top products.',
      },
    },
    businessSubTabs: {
      sales: 'Sales',
      customer: 'Customer',
    },
    customer: {
      listTitle: 'Customers',
      searchPlaceholder: 'Search customer…',
      emptyList: 'No customers purchased in this period.',
      selectPrompt: 'Select a customer to see their purchase history.',
      loading: 'Loading customer report…',
      detailLoading: 'Loading purchase history…',
      detailError: 'Failed to load purchase history.',
      orderLoadFailed: 'Failed to open the sales order.',
      chartTitle: 'Finished-Good Purchases (pcs)',
      emptyChart: 'No finished-good purchases in this period.',
      summary: {
        topFinishedGood: 'Most Purchased',
        totalPcs: 'Total Pcs',
        totalOrders: 'Total Orders',
        totalRevenue: 'Total Revenue',
        none: '—',
      },
      table: {
        orders: 'Orders',
        pcs: 'Pcs',
        lastOrder: 'Last Order',
      },
      history: {
        back: 'Back',
        date: 'Date',
        invoice: 'Invoice No',
        qty: 'Qty',
        total: 'Total Qty',
        empty: 'No purchases for this finished good.',
      },
    },
    export: {
      pdfSuccess: 'PDF report exported successfully.',
      pdfFailed: 'Failed to export PDF report.',
      excelSuccess: 'Excel report exported successfully.',
      excelFailed: 'Failed to export Excel report.',
      sheetName: 'Report',
      dataSourceNote: 'Data source: live API endpoints with automatic fallback data on request failures.',
      sheetCustomers: 'Customers',
      sheetSalesHistory: 'Sales History',
      subtotal: 'Subtotal',
      grandTotal: 'Grand Total',
      partialFailure: '{{count}} item(s) could not be loaded and were skipped.',
      chartSkipped: 'Chart could not be captured; exported without it.',
      columns: {
        roastingDate: 'Roasting Date',
        batchNumber: 'Batch Number',
        greenBeanCode: 'Green Bean Code',
        greenBeanName: 'Green Bean Name',
        inputWeight: 'Input Weight',
        outputWeight: 'Output Weight',
        weightLossPercent: 'Weight Loss %',
        itemCode: 'Item Code',
        itemName: 'Item Name',
        supplier: 'Supplier',
        remainingQuantity: 'Remaining Quantity',
        unit: 'Unit',
        averageCost: 'Average Cost',
        relatedBatch: 'Related Batch',
        lastProduction: 'Last Production',
        customerName: 'Customer Name',
        orderCount: 'Order Count',
        totalRevenue: 'Total Revenue',
        lastOrderDate: 'Last Order Date',
        totalPcs: 'Total Pcs',
        customerCode: 'Customer Code',
        finishedGood: 'Finished Good',
        orderNumber: 'Invoice No',
        orderDate: 'Order Date',
        totalAmount: 'Total Amount',
        status: 'Status',
      },
    },
    general: {
      to: 'to',
      filter: 'Filter',
      loadingData: 'Loading report data...',
      emptyPeriod: 'No data found for this period.',
      showingResults: 'Showing {{from}} to {{to}} of {{total}} results',
      table: {
        date: 'Date',
        type: 'Type',
        reference: 'Reference',
        amount: 'Amount',
        cost: 'Cost',
        profitLoss: 'Profit/Loss',
      },
    },
    business: {
      loading: 'Loading business report...',
      kpi: {
        totalRevenue: 'Total Revenue',
        totalOrders: 'Total Orders',
        activeCustomers: 'Active Customers',
        averageOrderValue: 'Average Order Value',
        netProfit: 'Net Profit',
      },
      revenueTrend: 'Revenue Trend',
      emptyTrend: 'No trend data available.',
      revenueByCustomer: 'Revenue by Customer',
      purchaseHistory: 'Sales History',
      emptyCustomerRevenue: 'No customer revenue data available.',
      emptyPurchaseHistory: 'No sales history available.',
      table: {
        customer: 'Customer',
        orders: 'Orders',
        revenue: 'Revenue',
        order: 'Order',
        amount: 'Amount',
      },
    },
    receivables: {
      title: 'Receivables',
      loading: 'Loading receivables...',
      loadFailed: 'Could not load receivables.',
      orderLoadFailed: 'Could not open that order.',
      totalLabel: 'Total receivable',
      mostOverdueLabel: 'Most overdue',
      noneOverdue: 'None overdue',
      asOf: 'As of {{date}}',
      empty: 'No outstanding receivables.',
      table: {
        orderNumber: 'Order #',
        customer: 'Customer',
        orderDate: 'Order date',
        dueDate: 'Due',
        daysOverdue: 'Overdue',
        amount: 'Amount',
      },
      daysValue: '{{days}} d',
    },
    roastedBeans: {
      loading: 'Loading roasted beans report...',
      empty: 'No roasted bean stock data available for selected range.',
      totalItems: 'Total Items',
      totalRemainingKg: 'Total Remaining (kg)',
      totalStockValue: 'Total Stock Value',
      table: {
        itemCode: 'Item Code',
        itemName: 'Item Name',
        relatedBatch: 'Related Batch',
        remaining: 'Remaining',
        avgCost: 'Avg Cost',
      },
    },
    finishedGoods: {
      loading: 'Loading finished goods report...',
      empty: 'No finished good stock data available for selected range.',
      totalItems: 'Total Items',
      totalRemainingPcs: 'Total Remaining (pcs)',
      totalStockValue: 'Total Stock Value',
      table: {
        itemCode: 'Item Code',
        itemName: 'Item Name',
        remaining: 'Remaining',
        avgCost: 'Avg Cost',
        lastProduction: 'Last Production',
      },
    },
    tracking: {
      title: 'Inventory Tracking',
      empty: 'No tracking history',
      error: 'Failed to load tracking history.',
      filterAll: 'All',
      filterIn: 'In',
      filterOut: 'Out',
      directionIn: 'In',
      directionOut: 'Out',
      table: {
        date: 'Date',
        direction: 'Direction',
        reference: 'Reference',
        counterparty: 'Counterparty',
        lot: 'Lot',
        quantity: 'Quantity',
      },
    },
    greenBeans: {
      totalItems: 'Total Items',
      totalRemainingKg: 'Total Remaining (kg)',
      totalStockValue: 'Total Stock Value',
      avgCost: 'Avg Cost',
      loading: 'Loading green beans report...',
      empty: 'No green bean stock data available for selected range.',
    },
    summary: {
      totalCost: 'Total Cost',
      grossProfit: 'Gross Profit',
      margin: 'Margin',
      vsLastMonth: 'vs last month',
    },
    roasting: {
      totalBatches: 'Total Batches',
      inputWeightKg: 'Input Weight (kg)',
      outputWeightKg: 'Output Weight (kg)',
      weightLossKg: 'Weight Loss (kg)',
      averageLossPercent: 'Average Loss (%)',
      inputKg: 'Input (kg)',
      outputKg: 'Output (kg)',
      lossPercent: 'Loss (%)',
      loading: 'Loading roasting report...',
      empty: 'No roasting data available for selected range.',
    },
    details: {
      detailsSuffix: 'Details',
      titleFallback: 'Details',
      revenueCalculation: 'Revenue Calculation',
      salesOrderTotalPrefix: 'Total amount from Sales Order',
      formulaRevenue: 'Revenue = Sum(Quantity × Unit Price)',
      profitCalculation: 'Profit Calculation',
      salesOrderProfitHelp: 'Sales orders are treated as revenue, adding to the overall profit. (Costs are recorded separately via Purchase Orders).',
      formulaProfitRevenue: 'Profit = Revenue',
      costCalculation: 'Cost Calculation',
      purchaseOrderTotalPrefix: 'Total amount from Purchase Order',
      formulaCost: 'Cost = Sum(Quantity × Unit Cost)',
      purchaseOrderProfitHelp: 'Purchase orders are treated as expenses, reducing the overall profit.',
      formulaProfitCost: 'Profit = -Cost',
      valueAdjustment: 'Value Adjustment',
      valueAdjustmentHelp: 'Inventory value change due to manual adjustment.',
      reason: 'Reason',
      formulaValueChange: 'Value Change = Quantity × Standard Cost',
      noDetails: 'No details available.',
      notes: 'Notes',
      unitCost: 'Unit Cost',
      orderItems: 'Order Items',
      loadingItems: 'Loading items...',
      qty: 'Qty',
      unitPrice: 'Unit Price',
      total: 'Total',
      noItems: 'No items found for this order.',
      howCalculated: 'How is this calculated?',
      actions: {
        editMovement: 'Edit Movement',
        saveChanges: 'Save Changes',
        cancelEdit: 'Cancel Edit',
      },
    },
    shift: {
      title: 'Shift Management',
      subtitle: 'Open and close shifts to track daily movements.',
      currentStatus: 'Current Status',
      loadingCurrent: 'Loading current shift...',
      errorCurrent: 'Failed to load current shift.',
      openTitle: 'Shift is Open',
      startedAt: 'Started at {{value}}',
      closeShift: 'Close Shift',
      openedByTitle: 'Opened by {{name}}',
      openedBy: 'Opened By',
      closedBy: 'Closed By',
      currentSales: 'Current Sales',
      cost: 'Cost',
      profit: 'Profit',
      transactions: 'Transactions',
      ordersCount: '{{count}} Orders',
      noActiveTitle: 'No Active Shift',
      noActiveDescription:
        'Start a new shift to begin tracking sales and inventory movements for the current period.',
      startShift: 'Start Shift',
      historyTitle: 'Shift History',
      loadingHistory: 'Loading history...',
      errorHistory: 'Failed to load history.',
      period: 'Period',
      totalSales: 'Total Sales',
      status: 'Status',
      view: 'View',
      closed: 'Closed',
      viewDetails: 'View Details',
      startNewTitle: 'Start New Shift',
      startNewDescription:
        'Are you sure you want to start a new shift? This will begin tracking all new transactions and inventory movements.',
      summaryTitle: 'Shift Summary',
      totalPurchases: 'Total Purchases',
      netCashFlow: 'Net Cash Flow',
      totalTransactions: 'Total Transactions',
      itemsIn: 'Items In',
      itemsOut: 'Items Out',
      financialTransactions: 'Financial Transactions',
      id: 'ID',
      type: 'Type',
      amount: 'Amount',
      time: 'Time',
      noTransactions: 'No transactions',
      inventoryMovements: 'Inventory Movements',
      item: 'Item',
      quantity: 'Quantity',
      reason: 'Reason',
      noMovements: 'No movements',
      reviewBeforeClose: 'Please review the summary before confirming closure.',
      onlyOpenerCanClose: 'Only {{name}} can close this shift',
      confirmClose: 'Confirm Close',
      toast: {
        closedSuccess: 'Shift closed successfully',
        closeFailed: 'Failed to close shift',
      },
    },
  },
  settingsMcp: {
    title: 'MCP Integrations',
    subtitle: 'Manage API keys for Model Context Protocol (MCP) agents to access your backend.',
    confirmRevoke: 'Are you sure you want to revoke this API key? This action cannot be undone.',
    errors: {
      loadKeys: 'Failed to load API keys',
      createKey: 'Failed to create API key',
      revokeKey: 'Failed to revoke API key',
    },
    connection: {
      title: 'AI Assistant Connection',
      connected: 'Connected — The AI assistant is using your API key to access your data.',
      disconnected: 'Disconnected — Generate a key and connect it to use the AI assistant with your account.',
      manualHelp: 'Paste an existing API key to connect the AI assistant:',
      manualPlaceholder: 'Paste your API key here...',
    },
    createForm: {
      title: 'Create New API Key',
      namePlaceholder: 'Key Name (e.g. Claude Desktop)',
    },
    newKey: {
      title: 'MCP API Key Created',
      description: 'Please copy and store this key now. It will only be shown once.',
      copyTooltip: 'Click copy button to copy key',
    },
    table: {
      name: 'Name',
      keyPrefix: 'Key Prefix',
      created: 'Created',
      lastUsed: 'Last Used',
      status: 'Status',
      actions: 'Actions',
    },
    docs: {
      title: 'Connection Details',
      description: 'To connect an MCP client (like Claude Desktop), use the following configuration:',
      replacePrefix: 'Replace',
      replaceSuffix: 'with a key generated above.',
      yourApiKeyPlaceholder: 'YOUR_API_KEY_HERE',
    },
    values: {
      none: '-',
    },
    status: {
      active: 'Active',
      revoked: 'Revoked',
    },
    actions: {
      generateNewKey: 'Generate New Key',
      disconnect: 'Disconnect',
      connectKey: 'Connect Key',
      connect: 'Connect',
      create: 'Create',
      copied: 'Copied',
      copyKey: 'Copy Key',
      connectToAssistant: 'Connect to AI Assistant',
      done: 'Done',
      copyPrefix: 'Copy Prefix',
      revoke: 'Revoke',
      copyToClipboard: 'Copy to clipboard',
    },
  },
  settingsMember: {
    title: 'Team Members',
    subtitle: 'Manage who has access to this workspace.',
    loading: 'Loading members...',
    fields: {
      email: 'Email',
      password: 'Password',
      emailAddress: 'Email Address',
      emailPlaceholder: 'colleague@company.com',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Jane Doe',
      role: 'Role',
    },
    values: {
      unknown: 'Unknown',
      you: '(You)',
    },
    roles: {
      admin: 'Admin',
      manager: 'Manager',
      operator: 'Operator',
      viewer: 'Viewer',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
    },
    actions: {
      inviteMember: 'Invite Member',
      sendInvite: 'Send Invite',
      copyPassword: 'Copy Password',
      done: 'Done',
      resetPassword: 'Reset Password',
      removeMember: 'Remove Member',
      reactivateMember: 'Reactivate Member',
      editRole: 'Edit Role',
      updateRole: 'Update Role',
      understand: 'I Understand',
      continue: 'Continue',
      back: 'Back',
    },
    errors: {
      sendInvite: 'Failed to send invitation',
      resetPassword: 'Failed to reset password',
      removeMember: 'Failed to remove member',
      reactivateMember: 'Failed to reactivate member',
      updateRole: 'Failed to update role',
      duplicateActive: 'A member with this email already exists in your company.',
      duplicateInactive: 'A deactivated member with this email already exists. You can reactivate them instead.',
      duplicateTitle: 'Email already in use',
      emailInUse: 'This email is already registered.',
    },
    toast: {
      memberAddedTitle: 'Member Added Successfully',
      passwordResetTitle: 'Password Reset Successful',
      roleUpdatedTitle: 'Role Updated Successfully',
      roleUpdatedMessage: 'Role updated for {{email}}',
    },
    passwordModal: {
      description: 'Please copy and share these credentials securely. The password will not be shown again.',
    },
    modals: {
      resetTitle: 'Reset Password',
      resetDescriptionPrefix: 'Are you sure you want to reset the password for',
      resetDescriptionSuffix: '? This action will generate a new random password and invalidate the old one.',
      removeTitle: 'Remove Member',
      removeDescriptionPrefix: 'Are you sure you want to remove',
      removeDescriptionSuffix: '? They will no longer be able to access the system.',
      removeWarning: 'This will deactivate the account. They will no longer be able to login, but you can reactivate them later.',
      reactivateTitle: 'Reactivate Member',
      reactivateDescription: 'Are you sure you want to reactivate',
      editRoleTitle: 'Edit Member Role',
      editRoleDescription: 'Update the role for',
      editRoleNote: 'The new role will take effect the next time this member signs in.',
      editRoleCurrentLabel: 'Current Role',
      editRoleNewLabel: 'New Role',
      adminInviteWarningTitle: 'Admin Role Cannot Be Changed',
      adminInviteWarningBody: 'You are inviting this member as an Admin. Admin role is permanent and cannot be changed later. Are you sure you want to continue?',
    },
    filters: {
      search: 'Search',
      searchPlaceholder: 'Search name or email...',
      allRoles: 'All Roles',
      allStatuses: 'All Statuses',
      noResults: 'No members found matching your search criteria.',
    },
  },
  settingsArtisan: {
    title: 'Company Artisan Integration',
    subtitle: 'Generate API keys for external Artisan exporter integrations owned by your company.',
    confirmRevoke: 'Are you sure you want to revoke this API key?',
    errors: {
      loadKeys: 'Failed to load Artisan API keys',
      createKey: 'Failed to create Artisan API key',
      revokeKey: 'Failed to revoke Artisan API key',
    },
    createForm: {
      title: 'Create New Artisan API Key',
      namePlaceholder: 'Key Name (e.g. Roaster Laptop)',
    },
    newKey: {
      title: 'Artisan API Key Created',
      description: 'Copy and store this key now. It will only be shown once.',
      copyTooltip: 'Click copy button to copy key',
    },
    table: {
      name: 'Name',
      keyPrefix: 'Key Prefix',
      created: 'Created',
      lastUsed: 'Last Used',
      status: 'Status',
      actions: 'Actions',
    },
    values: {
      none: '-',
    },
    status: {
      active: 'Active',
      revoked: 'Revoked',
    },
    actions: {
      generateNewKey: 'Generate New Key',
      create: 'Create',
      copied: 'Copied',
      copyKey: 'Copy Key',
      done: 'Done',
      revoke: 'Revoke',
    },
  },
  forms: {
    common: {
      autoGenerated: 'Auto-generated',
      generatedAutomatically: 'Generated automatically',
      email: 'Email',
      phone: 'Phone',
      status: 'Status',
      statusActive: 'Active',
      statusInactive: 'Inactive',
      internalNotes: 'Internal Notes',
      loadingHistory: 'Loading history...',
      date: 'Date',
      amount: 'Amount',
      poNumber: 'PO Number',
      orderNumber: 'Order #',
      all: 'All',
    },
    actions: {
      sendEmail: 'Send Email',
      openWhatsapp: 'Open WhatsApp',
      call: 'Call',
      close: 'Close',
      saving: 'Saving...',
      save: 'Save',
    },
    supplier: {
      name: 'Name',
      code3: 'Code (3-Char)',
      contactName: 'Contact Name',
      purchaseOrderHistory: 'Purchase Order History',
      emptyPurchaseOrders: 'No purchase orders found for this supplier.',
      poStatus: {
        draft: 'Draft',
        ordered: 'Ordered',
        cancelled: 'Cancelled',
      },
    },
    customer: {
      name: 'Name',
      code3: 'Code (3-Char)',
      billingAddress: 'Billing Address',
      shippingAddress: 'Shipping Address',
      sameAsBilling: 'Same as billing address',
      messageOnInvoice: 'Message on Invoice',
      salesOrderHistory: 'Sales Order History',
      emptySalesOrders: 'No sales orders found for this customer.',
      emailCounter: '{{count}}/{{max}} chars',
      hints: {
        enterEmailToSend: 'Enter email to send message',
        enterPhoneForWhatsapp: 'Enter phone number for WhatsApp',
        enterPhoneForCall: 'Enter phone number for Call',
      },
      soStatus: {
        Draft: 'Draft',
        Sent: 'Sent',
        Delivered: 'Delivered',
        Cancelled: 'Cancelled',
      },
      itemName: 'Item Name',
    },
  },
  recipes: {
    form: {
      preheat: 'Preheat',
      recipeName: 'Recipe Name',
      internalNotesPlaceholder: 'Internal notes only visible to staff...',
      targetBeanWeight: 'Target Bean Weight',
      totalTimeMinutes: 'Total Time (minutes)',
      shelfLifeDays: 'Shelf Life (days)',
      roastStages: 'Roast Stages',
      addStage: 'Add Stage',
      stageName: 'Stage Name',
      stageNamePlaceholder: 'e.g. Drying',
      tempC: 'Temp (°C)',
      timeMin: 'Time (min)',
      update: 'Update',
      create: 'Create',
    },
    roastProfileChart: {
      addStagesToView: 'Add roast stages to view profile',
      visualization: 'Roast Profile Visualization',
    },
    tasting: {
      title: 'Edit Tasting Notes',
      flavorTags: 'Flavor Tags',
      generalNotes: 'General Notes',
      placeholderTag: 'Type to add a flavor tag...',
      placeholderNotes: 'Describe the overall impression...',
      saveNotes: 'Save Notes',
      removeTag: 'Remove {{tag}}',
      attributes: {
        Acidity: 'Acidity',
        Aftertaste: 'Aftertaste',
        'Clean Cup': 'Clean Cup',
        Head: 'Head',
        Fragrance: 'Fragrance',
        Sweetness: 'Sweetness',
        Balance: 'Balance',
        Aroma: 'Aroma',
        Body: 'Body',
      },
    },
    tastingSection: {
      title: 'Tasting Profile',
      empty: 'No tasting notes added yet.',
      addNotes: 'Add Tasting Notes',
      editNotes: 'Edit Notes',
      noTags: 'No tags selected',
      noNotes: 'No notes provided',
    },
    page: {
      loading: 'Loading recipes...',
      searchPlaceholder: 'Search profiles...',
      empty: 'No profiles found matching your filters.',
      fetchingDetails: 'Fetching profile details...',
      columns: {
        itemCode: 'Item & Code',
        targetWeightInOut: 'Target Weight (In/Out)',
      },
      actions: {
        newProfile: 'New Profile',
        updateProfile: 'Update Profile',
        createNewProfile: 'Create New Profile',
        deleting: 'Deleting...',
      },
      errors: {
        loadFailed: 'Failed to load recipes. Please try again.',
        deleteFailed: 'Failed to delete profile. Please try again.',
      },
      moreCount: '+{{count}} more',
      shelfLifeDaysCount: '{{days}} days',
      viewDetailsArrow: 'View Details →',
      confirmDeletePrefix: 'Are you sure you want to delete profile',
    },
  },
  greenBeanProfile: {
    title: 'Green Bean Profiles',
    loading: 'Loading green bean profiles...',
    search: 'Search',
    searchPlaceholder: 'Search by Profile Code, Name, Origin, Country...',
    itemName: 'Item Name',
    allItems: 'All Items',
    selectedCount: '{{count}} selected',
    emptyItems: 'No items found',
    emptyProfiles: 'No profiles found.',
    grade: 'Grade {{grade}}',
    values: {
      noCode: 'No Code',
      noName: 'No Name',
      defaultProfile: 'Default Profile',
    },
    actions: {
      new: 'New Green Bean',
      edit: 'Edit',
      delete: 'Delete',
      update: 'Update Profile',
      create: 'Create Profile',
    },
    errors: {
      loadFailed: 'Failed to load profiles',
      deleteFailed: 'Failed to delete profile',
      saveFailed: 'Failed to save profile. Please try again.',
      applyPresetFailed: 'Failed to apply item profile preset. Please try again.',
      skipPresetFailed: 'Failed to close preset popup. Please try again.',
    },
    useItemProfileTitle: 'Use item green bean profile?',
    useItemProfileMessage: 'This lot item already has a green bean profile. Do you want to use it?',
    deleteModal: {
      title: 'Delete Profile',
      message: 'Are you sure you want to delete this green bean profile? This action cannot be undone.',
    },
  },
  greenBean: {
    quality: {
      title: 'Quality & Grading',
      grade: 'Grade',
      gradeLabel: 'Grade {{grade}}',
      color: 'Color',
      moistureContent: 'Moisture Content (%)',
      qcCupping: 'QC Cupping',
      selectType: 'Select Type',
      aromaMulti: 'Aroma (Select multiple)',
      tasteProfile: 'Taste Profile',
      tasteProfilePlaceholder: 'Describe the taste profile...',
      errors: {
        gradeRequired: 'Grade is required',
        colorRequired: 'Color is required',
      },
    },
    physical: {
      title: 'Physical Assessment',
      selectGrade: 'Select Grade',
      selectColor: 'Select color',
      density: 'Density (g/L)',
      densityPlaceholder: 'e.g. 720',
      errors: {
        moistureRequired: 'Moisture content is required',
      },
    },
    processing: {
      title: 'Processing',
      processorName: 'Processor Name',
      selectProcessType: 'Select Process Type',
      otherDetails: 'Other Processing Details',
      notes: 'Processing Notes',
    },
    itemInfo: {
      title: 'Item Information',
      workOrder: 'Work Order',
    },
    origin: {
      title: 'Origin',
      notes: 'Origin Notes',
    },
    extrinsic: {
      title: 'Extrinsic',
      country: 'Country',
      province: 'Province',
      region: 'Region',
      farm: 'Name of Farm or Co-op',
      processor: 'Name of Processor(s)',
      wetMill: 'Wet Mill / Station',
      dryMill: 'Dry Mill',
      other: 'Other',
      harvestDateYear: 'Harvest Date / Year',
      placeholderCountry: 'Select or type country',
      placeholderProvince: 'Select or add province',
      placeholderRegion: 'Select or add region',
      placeholderFarm: 'Select or add farm',
      placeholderProcessor: 'Select or add processor',
      errors: {
        specifyProcessType: 'Please specify process type',
      },
    },
  },
  settingsBilling: {
    title: 'Billing & Usage',
    proPlanActive: 'Pro Plan Active',
    currentPlan: 'Current Plan',
    planName: 'Pro Plan',
    planBilling: '$29.00 / month • Billed monthly',
    nextBillingDate: 'Next billing date',
    nextBillingDateValue: 'November 1, 2023',
    usageLimits: 'Usage Limits',
    paymentMethod: 'Payment Method',
    visaEnding: 'Visa ending in 4242',
    expires: 'Expires 12/2024',
    cardNumber: 'Card Number',
    expiryDate: 'Expiry Date',
    cvc: 'CVC',
    billingHistory: 'Billing History',
    invoice: 'Invoice',
    pdf: 'PDF',
    metrics: {
      Storage: 'Storage',
      'Team Members': 'Team Members',
      'API Calls': 'API Calls',
    },
    status: {
      paid: 'Paid',
      pending: 'Pending',
      failed: 'Failed',
    },
    actions: {
      update: 'Update',
      saveCard: 'Save Card',
    },
    placeholders: {
      cardNumber: '0000 0000 0000 0000',
      expiry: 'MM/YY',
      cvc: '123',
    },
    toast: {
      cardUpdated: 'Card details updated!',
    },
  },
  settingsAdminMembers: {
    title: 'Admin Member',
    subtitle: 'Platform-wide admin accounts across all companies.',
    loading: 'Loading admin members...',
    retry: 'Retry',
    empty: 'No admin members found.',
    noResults: 'No admin members match your search.',
    searchPlaceholder: 'Search by email, full name, or company...',
    companyName: 'Company Name',
    unknownCompany: 'Unknown Company',
    errors: {
      loadFailed: 'Failed to load admin members.',
      unableLoad: 'Unable to load admin members',
    },
  },
  settingsCompany: {
    title: 'Company Profile',
    subtitle: "Update your company's information. This will be used on invoices and other documents.",
    logo: 'Company Logo',
    removeLogo: 'Remove logo',
    companyName: 'Company Name',
    address: 'Address',
    website: 'Website',
    websitePlaceholder: 'https://example.com',
    currencySymbol: 'Currency Symbol',
    currencyPlaceholder: 'e.g. Rp, $, €',
    savedSuccess: 'Saved successfully!',
    errors: {
      updateFailed: 'Failed to update company profile',
      logoMaxSize: 'Logo must be less than 2MB',
      logoType: 'Logo must be a PNG, JPG, or WEBP image',
      uploadFailed: 'Failed to upload logo',
      deleteFailed: 'Failed to delete logo',
    },
  },
  roast: {
    profile: {
      stages: {
        start: 'Start',
        turningPoint: 'Turning Point',
        yellow: 'Yellow',
        firstCrack: 'First Crack',
        end: 'End',
        preheat: 'Preheat',
      },
      legend: {
        beanTemp: 'Bean Temp',
        ror: 'ROR',
        fire: 'Fire',
        airflow: 'Airflow',
        airTemp: 'Air Temp',
        drumTemp: 'Drum Temp',
        environmentTemp: 'Environment Temp',
        drumRpm: 'Drum RPM',
      },
      phases: {
        drying: 'Drying',
        maillard: 'Maillard',
        development: 'Development',
      },
      markers: {
        charge: 'CHARGE',
        tp: 'TP',
        fcs: 'FCs',
        drop: 'DROP',
      },
    },
    agtron: {
      targetAgtronMeter: 'Target Agtron Meter',
      agtronMeter: 'Agtron Meter',
      wholeBean: 'Whole Bean',
      groundBean: 'Ground Bean',
      difference: 'Difference',
      scaa: 'SCAA',
      common: 'Common',
      zeroPlaceholder: '0',
    },
    weightAnalysis: {
      targetWeightAnalysis: 'Target Weight Analysis',
      weightAnalysis: 'Weight Analysis',
      differsCalculated: 'Warning: Differs from calculated ({{weight}} {{unit}})',
      largeShrinkage: 'Warning: Large shrinkage',
      outputGreater: 'Roast output cannot be greater than roast input.',
    },
    timePhases: {
      targetTimePhases: 'Target Time Phases',
      timePhases: 'Time Phases',
      dryingPhase: 'Drying Phase',
      startToYellow: 'Start to Yellow',
      maillardPhase: 'Mailard Phase',
      yellowToFirstCrack: 'Yellow to 1st Crack',
      developmentPhase: 'Development Phase',
      firstCrackToEnd: '1st Crack to End',
    },
    lotSection: {
      lotsUsed: 'Lots Used',
      addLot: 'Add Lot',
      totalUsedWeight: 'Total Used Weight ({{unit}})',
      totalUsedWeightMismatch: 'Total used weight from lots different with Roast Input',
      selectLot: 'Select Lot',
      density: 'Density (gr/L)',
      processType: 'Process Type',
      before: 'Before',
      after: 'After',
      useWeight: 'Use Weight',
      exceedsRemainingStock: 'Exceeds remaining stock',
      afterUse: 'After Use',
    },
    stageData: {
      targetStageData: 'Target Stage Data',
      stageData: 'Stage Data',
      temperature: 'Temperature',
      celsius: '°C',
      mask: {
        minute: 'M',
        second: 'S',
      },
    },
    formNotes: {
      enterNotes: 'Enter notes here...',
    },
    page: {
      title: 'Roasting',
      newRoast: 'New Roast',
      createNewRoast: 'Create New Roast',
    },
    form: {
      unnamedProfile: 'Unnamed Profile',
      unknownBean: 'Unknown Bean',
      errors: {
        saveRecipeFailed: 'Failed to save recipe',
        saveRoastFailed: 'Failed to save roast',
      },
      success: {
        savedSuccessfully: 'Roast saved successfully!',
      },
    },
    profileTable: {
      additionalColumn: 'Additional Column',
      noneSelected: 'No Selected',
      selected: 'selected',
      selectedCount: '{{count}} selected',
      addRow: 'Add Row',
      manualTimeEntry: 'Manual Time Entry',
      min: 'Min',
      sec: 'Sec',
      addToProfile: 'Add to Profile',
      temp: 'Temp',
      ror: 'ROR',
      fire: 'Fire',
      airflow: 'Airflow',
      airTemp: 'Air Temp',
      drumTemp: 'Drum Temp',
      environmentTemp: 'Environment Temp',
      drumRpm: 'Drum RPM',
      removeRow: 'Remove this row',
      edits: {
        summaryOne: '1 edited by hand',
        summaryMany: '{{count}} edited by hand',
        resetAll: 'Reset all',
        reset: 'Reset',
        resetField: 'Reset {{field}}',
        revertTo: 'Restore imported value {{value}}',
        revertToEmpty: 'Restore this cell to empty',
        andMore: '+{{count}} more',
      },
      errors: {
        secondsRange: 'Seconds must be 0-59.',
        invalidTime: 'Invalid time.',
        afterLastRow: 'Time must be after the last row ({{lastTime}})',
        within30s: 'Time must be within 30s after the last row (before {{before}})',
      },
    },
    profileForm: {
      roastDate: 'Roast Date',
      required: 'Required',
      errors: {
        invalidJson: 'Invalid JSON file. Please ensure the file contains a valid roast profile structure.',
        entityTypeMissing: 'Internal Error: Entity Type is missing',
        saveFailed: 'Failed to save roast profile',
      },
    },
    formHeader: {
      profileName: 'Profile Name',
      enterProfileName: 'Enter profile name',
      machine: 'Machine',
      selectMachineType: 'Select machine type',
      beanItem: 'Bean Item',
      selectBean: 'Select a bean',
      roastNumber: 'Roast Number',
      recipeName: 'Recipe Name',
      enterRecipeName: 'Enter recipe name',
      roaster: 'Roaster',
      enterRoasterName: 'Enter roaster name',
    },
  },
  cuppingSessions: {
    title: 'Cupping Sessions',
    searchPlaceholder: 'Search cupping code or cupper...',
    loading: 'Loading cupping sessions...',
    empty: 'No cupping sessions found matching your filters.',
    columns: {
      code: 'Code',
      cupper: 'Cupper',
      items: 'Items',
      draftDate: 'Draft Date',
      completeDate: 'Complete Date',
    },
    status: {
      Draft: 'Draft',
      Complete: 'Complete',
      all: 'All',
    },
    actions: {
      new: 'New Cupping Session',
      open: 'Open',
    },
    create: {
      title: 'New Cupping Session',
      loadingData: 'Loading data...',
      selectCupper: 'Select cupper',
      cuppingMode: 'Cupping Mode',
      modeLockedNote: '*This cannot be changed after creation.',
      roastedBulkSelection: 'Roasted Bulk Selection',
      searchBulkPlaceholder: 'Search work order, item name, roast type',
      allBulksAvailable: 'All roasted bulks are available.',
      practiceRoastLimit: 'The practice cupping compares two roasts. Remove one below to pick a different roast.',
      enableBlindMode: 'Enable blind mode',
      blindCode: 'Blind Code',
      noCode: 'No Code',
      saveDraft: 'Save Draft',
      confirmTitle: 'Confirm Cupping Mode',
      confirmLock: 'Cupping mode cannot be changed later',
      selectedModePrefix: 'You selected',
      selectedModeSuffix: 'mode. This will be locked for the entire session.',
      preview: 'Preview',
      previewMode: 'Preview {{mode}}',
      formPreviewDisabled: 'Form Preview (Disabled)',
      readOnly: 'READ-ONLY',
      closePreview: 'Close Preview',
      errors: {
        selectCupper: 'Please select a cupper.',
        selectMinItems: 'Please select at least 2 roasted bulk items.',
        itemInDraft: {
          title: 'Item already in a draft',
          intro: 'The following roasted bulk item(s) are already attached to another draft cupping session and cannot be reused until that draft is completed or removed:',
          inSession: 'in draft {{code}}',
          hint: 'Open the conflicting draft and either complete it or remove the item, then try again.',
          dismiss: 'OK, got it',
        },
        unknown: 'Could not save the cupping session. Please try again.',
      },
    },
    form: {
      errors: {
        completeAllTabs: 'Please complete all item tabs before completing the session.',
      },
      loadingSession: 'Loading cupping session...',
      notFound: 'Cupping session not found.',
      backToSessions: 'Back to sessions',
      blindModeEnabled: 'Blind Mode Enabled',
      standardMode: 'Standard Mode',
      clickToViewDetails: 'Click to view item details',
      holdToViewDetails: 'Hold to view item details',
      reveal: 'Reveal',
      completedCount: '{{completed}}/{{total}} completed',
      completeSession: 'Complete Session',
      confirmCompleteTitle: 'Confirm Complete Session',
      profilesLockedTitle: 'Profiles cannot be edited after completion',
      profilesLockedMessage: 'Once this session is completed, all cupping profiles will be permanently locked and cannot be modified. If you need to make changes, you will need to create new cupping profiles.',
      roastViewer: {
        title: 'Work Order Roast Profile',
        workOrderNotFound: 'Work order not found.',
        noRoastProfile: "This work order doesn't have roast profile.",
        dataLog: 'Roast Data Log',
        profileCurve: 'Roast Profile Curve',
        notes: 'Notes',
        header: {
          workOrder: 'Work Order',
          machine: 'Machine',
          roaster: 'Roaster',
          completed: 'Completed',
          roastSuffix: 'Roast',
        },
        table: {
          time: 'Time',
          temp: 'Temp',
          ror: 'ROR',
        },
        analysis: {
          in: 'In',
          out: 'Out',
          loss: 'Loss',
          whole: 'Whole',
          ground: 'Ground',
          diffType: 'Diff / Type',
        },
        beans: {
          title: 'Green Bean & Lots',
          greenBean: 'Green Bean',
          lots: 'Lots',
          noLots: 'No lots recorded.',
        },
      },
    },
  },
  cuppingProfileForm: {
    confirmSaveTitle: 'Confirm Save Profile',
    lockedTitle: 'Profile cannot be edited after saving',
    lockedMessage: 'Once saved, this cupping profile will be permanently locked and cannot be modified. If you need to make changes, you will need to create a new cupping profile.',
    errors: {
      selectDefectTypeWhenDefectiveCups: 'Please select at least one defect type when "Defective Cups" > 0.',
    },
  },
  lotSelector: {
    loading: 'Loading lots...',
    empty: 'No lots available',
    selectLots: 'Select Lots',
    needMore: 'Need {{amount}} {{unit}} more',
    requirementMet: 'Requirement Met',
    expires: 'Expires',
    qtyPlaceholder: 'Qty',
    allocatedExceedsRequired: 'Warning: Allocated quantity ({{allocated}}) exceeds required ({{required}})',
    errors: {
      loadFailed: 'Failed to load lots',
    },
  },
  profileManager: {
    greenBean: {
      edit: 'Edit Green Bean Profile',
      add: 'Add Green Bean Profile',
    },
    roast: {
      edit: 'Edit Roast Profile',
      add: 'Add Roast Profile',
    },
    cupping: {
      add: 'Add Cupping Profile',
      view: 'View Cupping Profile',
    },
    actions: {
      add: 'Add',
    },
    selectCuppingDate: 'Select Cupping Date',
    itemHasCuppingProfiles: 'This item has {{count}} cupping profile. Select a date to view:',
    viewOtherProfiles: 'View Other Profiles',
  },
  finishedGoodsProduction: {
    modal: {
      createDraft: 'Create Finished Goods Draft',
      completeDraft: 'Complete Finished Goods Draft',
      viewDraft: 'View Finished Goods Draft',
      useExisting: 'Use Existing',
      createNew: 'Create New',
      finalQuantityPcs: 'Final Quantity (pcs)',
      calculateMaxQty: 'Calculate max qty',
      completeRequiredToSave: 'Complete required fields to enable Save.',
      processing: 'Processing...',
      complete: 'Complete',
      workOrderOnlyNotice: 'Only roasted bulk produced from a completed work order, or imported with a green-bean source, can be selected as a component.',
      closeConfirmTitle: 'Close Finished Goods Form',
      closeConfirmMessage: 'Any unsaved changes will be lost. Do you want to continue?',
      keepEditing: 'Keep editing',
      discardChanges: 'Discard changes',
    },
    errors: {
      componentRequired: 'Roasted bulk component is required.',
      allocationInvalid: 'Allocation must be complete, total 100%, and match the pack size target.',
      exceedsMaxQty: 'Quantity exceeds the maximum capacity ({{maxQuantity}}). Please reduce it or click Calculate max qty.',
      loadDataFailed: 'Failed to load finished goods production data',
      missingProductionId: 'Missing production id',
      loadDraftFailed: 'Failed to load production draft. Please try again.',
    },
  },
  qcCupping: {
    modeSimplified: 'Simplified Cupping',
    modeFull: 'Full Version (CVA Descriptive Form)',
    scaleSimplified: 'Scale 1-3 (Overall 1-9)',
    scaleFull: 'Scale 1-15',
    flavorNotes: 'Flavor Notes (CATA Boxes)',
    notes: 'Notes (CATA Boxes)',
    mouthfeelNotes: 'Mouthfeel Notes (CATA Boxes)',
  },
  batch: {
    list: {
      placeholder: 'Hello',
    },
  },
  cupping: {
    modes: {
      upgradeRequired: 'Upgrade to basic tier to use this mode',
    },
    formSections: {
      lockSection: 'Lock Section',
      unlockSection: 'Unlock Section',
      mode: {
        simplified: 'Simplified',
        descriptive: 'Descriptive Form',
        affective: 'Affective Form',
      },
      labels: {
        fragranceIntensity: 'Fragrance Intensity',
        aromaIntensity: 'Aroma Intensity',
        flavor: 'Flavor',
        aftertaste: 'Aftertaste',
        mainTastes: 'Main Tastes',
        max2: '(max 2 options)',
        flavorAftertasteNotes: 'Flavor/Aftertaste Notes',
        acidity: 'Acidity',
        intensityOnly: 'Intensity only.',
        acidityNotes: 'Acidity Notes',
        sweetness: 'Sweetness',
        sweetnessNotes: 'Sweetness Notes',
        mouthfeel: 'Mouthfeel',
        attributes: 'Attributes',
        mouthfeelNotes: 'Mouthfeel Notes',
        physicalCheckPassed: 'Physical Check Passed',
        yes: 'YES',
        no: 'NO',
        defects: 'Defects',
        balance: 'Balance',
        overall: 'Overall',
        saveProfile: 'Save Profile',
      },
    },
    sca: {
      fragrance: 'Fragrance',
      aroma: 'Aroma',
      nonUniformCups: 'Number of Non-Uniform Cups',
      defectiveCups: 'Number of Defective Cups',
      defectsIfAny: 'Defects (if any)',
      score: 'Score',
    },
    simplified: {
      dryAcidity: 'Dry Acidity (Herby, Grassy, Tart)',
      sweetAcidity: 'Sweet Acidity (Juicy, Fruit-like, Bright)',
      notBalance: 'Not balance',
      wellBalance: 'Well balance',
      roastDefects: 'Roast Defects',
      underdevelopment: 'Underdevelopment',
      overdevelopment: 'Overdevelopment',
      baked: 'Baked',
      scorched: 'Scorched',
    },
    formFields: {
      roastLevel: 'Roast Level',
      light: 'Light',
      medium: 'Medium',
      dark: 'Dark',
      low: 'Low',
      high: 'High',
    },
  },
  chatWidget: {
    welcome: "Hi! I'm your ERP Assistant. I can help you create orders, check inventory, or analyze sales. How can I help you today?",
    openAria: 'Open AI Assistant',
    askAiAssistant: 'Ask AI Assistant',
    erpAssistant: 'ERP Assistant',
    online: 'Online',
    typeMessage: 'Type a message...',
    disclaimer: 'AI can make mistakes. Please verify important information.',
  },
  notifications: {
    settings: {
      title: 'Notification Reminders',
      description: 'Configure how many times and when reminders are sent before a roast starts.',
      addReminder: 'Add reminder',
      maxReached: 'Max 5 reminders reached',
      enableToggle: 'Send reminders for new work orders',
      unitMinutes: 'minutes',
      unitHours: 'hours',
      unitDays: 'days',
      beforeStart: 'before start',
      resyncButton: 'Re-sync existing pending WOs',
      saveSuccess: 'Settings saved',
    },
    bell: {
      title: 'Notifications',
      empty: 'No notifications yet',
      markAllRead: 'Mark all read',
    },
    toast: {
      newIncoming: 'New notification',
      workOrderReminder: {
        title: 'Roasting starts in {{offsetLabel}}',
        body: '{{woNumber}} · {{roastType}} · {{weight}} kg',
      },
    },
    offsetLabel: {
      minutes: 'minutes',
      hours: 'hours',
      days: 'days',
    },
  },
  roles: {
    page: {
      title: 'Roles & Permissions',
      subtitle: 'Manage which actions each role can perform across your workspace.',
      newRole: 'New Role',
      empty: 'No roles yet — create one to get started.',
      upgradeTooltip: 'Upgrade to customize roles',
      upgradeBanner:
        'Your current plan is read-only for role management. Upgrade to create or customize roles.',
      sectionSystem: 'Built-in Roles',
      sectionSystemHint: 'Preset roles with full access. Review their scope but cannot be edited.',
      sectionCustom: 'Custom Roles',
      sectionCustomHint: 'Roles tailored to your team. Editable and safe to delete once empty.',
      actions: 'Actions',
    },
    badges: {
      system: 'System role',
      default: 'Default',
    },
    table: {
      displayName: 'Role',
      canonicalName: 'Canonical Name',
      memberCount: 'Members',
      createdAt: 'Created',
    },
    delete: {
      title: 'Delete role?',
      confirmPrefix: 'You are about to delete',
      hasMembers: 'Reassign {{count}} members before deleting',
      reassignFirst: 'Reassign members first',
      failed: 'Failed to delete role',
    },
    editor: {
      createTitle: 'Create Role',
      editTitle: 'Edit Role',
      viewTitle: 'View Role',
      displayName: 'Role Name',
      displayNamePlaceholder: 'e.g. Warehouse Supervisor',
      description: 'Description',
      descriptionPlaceholder: 'What is this role responsible for?',
      systemRoleNote:
        'System roles have full access to every resource. Their permissions cannot be edited.',
      upgradeBanner: 'Upgrade your plan to customize roles.',
      resetDefaults: 'Reset to defaults',
      resetConfirmTitle: 'Reset to default permissions?',
      resetConfirmBody:
        'This replaces all current permissions with the canonical defaults. Continue?',
      resetConfirm: 'Reset',
      saveFailed: 'Failed to save role',
      resetFailed: 'Failed to reset role',
      permissionsTitle: 'Permissions',
      permissionsSubtitle: 'Toggle which actions this role can perform on each resource.',
      searchPlaceholder: 'Search resources…',
      noMatches: 'No resources match your search.',
      selectedSummary: '{{selected}} of {{total}} permissions enabled',
      readOnlySummary: '{{total}} permissions in total',
    },
    grid: {
      resource: 'Resource',
      action: {
        read: 'Read',
        write: 'Write',
        delete: 'Delete',
      },
      quick: 'Quick',
      grantAll: 'Grant all',
      clear: 'Clear',
      unsupported: 'N/A',
    },
    categories: {
      operations: 'Operations',
      profiles: 'Profiles & Quality',
      sales: 'Sales & Customers',
      team: 'Team & Assets',
      intelligence: 'Intelligence & Reports',
      company: 'Company',
      other: 'Other',
    },
    resources: {
      purchase_orders: 'Purchase Orders',
      suppliers: 'Suppliers',
      inventory: 'Inventory',
      green_bean_profiles: 'Green Bean Profiles',
      taste_profiles: 'Taste Profiles',
      roast_profiles: 'Roast Profiles',
      work_orders_roasting: 'Work Orders — Roasting',
      work_orders_packing: 'Work Orders — Packing',
      recipes: 'Recipes',
      artisan_profiles: 'Artisan Profiles',
      cupping_sessions: 'Cupping Sessions',
      sales_orders: 'Sales Orders',
      customers: 'Customers',
      members: 'Team Members',
      machines: 'Machines',
      machine_types: 'Machine Types',
      roasters: 'Roasters',
      qora_assistant: 'Qora AI Assistant',
      dashboard_avg_shrinkage: 'Dashboard — Avg Shrinkage',
      dashboard_outstanding_payment: 'Dashboard — Outstanding Payment',
      dashboard_revenue: 'Dashboard — Revenue',
      dashboard_expenses: 'Dashboard — Expenses',
      dashboard_net_profit: 'Dashboard — Net Profit',
      dashboard_green_bean_value: 'Dashboard — Green Bean Stock Value',
      dashboard_roasted_value: 'Dashboard — Roasted Stock Value',
      dashboard_finished_value: 'Dashboard — Finished Goods Value',
      dashboard_species: 'Dashboard — Species Breakdown',
      dashboard_varietas: 'Dashboard — Variety Breakdown',
      dashboard_recent_activities: 'Dashboard — Recent Activities',
      dashboard_expiring_soon: 'Dashboard — Expiring Soon',
      dashboard_low_stock: 'Dashboard — Low Stock',
      reports_roasting: 'Reports — Roasting',
      reports_green_beans: 'Reports — Green Beans',
      reports_roasted_beans: 'Reports — Roasted Beans',
      reports_finished_goods: 'Reports — Finished Goods',
      reports_business: 'Reports — Business',
      reports_customer: 'Reports — Customer',
      activity_logs: 'Activity Logs',
      company: 'Company Info',
    },
    resourceDescriptions: {
      purchase_orders: 'Draft, send, and track orders to suppliers.',
      suppliers: 'Supplier directory, contacts, and trading history.',
      inventory: 'On-hand stock, lots, and inventory adjustments.',
      green_bean_profiles: 'Origin and quality profiles for raw beans.',
      taste_profiles: 'Tasting notes and flavor profiles.',
      roast_profiles: 'Saved roasting curves and profiles.',
      work_orders_roasting: 'Create and schedule roasting work orders.',
      work_orders_packing: 'Create and manage finished-good packing production drafts.',
      recipes: 'Blend and product recipe definitions.',
      artisan_profiles: 'Artisan logger export profiles.',
      cupping_sessions: 'Cupping sessions and scoring sheets.',
      sales_orders: 'Customer sales orders and fulfillment.',
      customers: 'Customer directory and contacts.',
      members: 'Invite, reset, and manage company members.',
      machines: 'Roasting machines and equipment.',
      machine_types: 'Machine type definitions.',
      roasters: 'Roaster operator profiles.',
      qora_assistant: 'Qora AI conversational assistant.',
      dashboard_avg_shrinkage: 'Average roasting weight loss KPI on the dashboard.',
      dashboard_outstanding_payment: 'Unpaid sales order amount KPI on the dashboard.',
      dashboard_revenue: 'Revenue KPI on the dashboard.',
      dashboard_expenses: 'Expenses KPI on the dashboard.',
      dashboard_net_profit: 'Net profit KPI on the dashboard.',
      dashboard_green_bean_value: 'Green bean stock valuation on the dashboard.',
      dashboard_roasted_value: 'Roasted bean stock valuation on the dashboard.',
      dashboard_finished_value: 'Finished goods stock valuation on the dashboard.',
      dashboard_species: 'Species breakdown infographic on the dashboard.',
      dashboard_varietas: 'Variety breakdown infographic on the dashboard.',
      dashboard_recent_activities: 'Recent activity feed on the dashboard.',
      dashboard_expiring_soon: 'Expiring-soon inventory alert on the dashboard.',
      dashboard_low_stock: 'Low-stock inventory alert on the dashboard.',
      reports_roasting: 'Roasting batch reports and weight-loss analytics.',
      reports_green_beans: 'Green bean inventory and supplier reports.',
      reports_roasted_beans: 'Roasted bean inventory and production reports.',
      reports_finished_goods: 'Finished good inventory and production reports.',
      reports_business: 'Customer, revenue, and shift performance reports.',
      reports_customer: 'Per-customer purchase history and product-mix reports.',
      activity_logs: 'Audit log of user and system activity.',
      company: 'Company profile, logo, and preferences.',
    },
  },
  subscription: {
    banner: {
      free: 'You are on the Free tier. Some advanced features require an upgrade.',
      warning: 'Your subscription ends in {{days}} days.',
      cta_claim: 'Claim Voucher',
      cta_renew: 'Renew',
    },
    page_subtitle: 'Manage your subscription tier and redeem voucher codes.',
    tier_pro: 'Basic',
    tier_free: 'Free',
    tier: {
      free: 'Free',
      basic: 'Basic',
      plus: 'Plus',
      tooltip: {
        title: {
          basic: 'Basic Plan',
          plus: 'Plus Plan',
        },
        expiryLabel: 'Expires on',
        daysLeft: '{{days}} days left',
        manage: 'Manage subscription',
        upgradeNow: 'Upgrade now',
        freeHint: "You're on the free plan",
      },
    },
    hero_pro_title: 'You have full access',
    hero_pro_subtitle: 'All advanced features (cupping modes, integrations, higher roast quotas) are unlocked.',
    hero_free_title: 'You are on the Free plan',
    hero_free_subtitle: 'Get all access to basic modules. Roast quota is 30 kg/month. Redeem a voucher to upgrade.',
    usage: {
      title: 'Roasting usage this month',
      cycleLabel: '{{start}} – {{end}}  ·  Resets {{nextStart}}',
      usedOf: '{{used}} / {{limit}} kg',
      remaining: 'Remaining: {{remaining}} kg',
      upgradeForMore: 'Upgrade for more →',
    },
    errors: {
      quotaExceeded: "You've used {{used}}/{{limit}} kg this month. Resets {{date}}.",
    },
    quotaModal: {
      title: 'Roasting quota reached',
      eyebrow: 'Heads up',
      bodyFree: "Oops! You've exceeded this month's work order usage. Upgrade to Basic to keep roasting.",
      bodyBasic: "Oops! You've exceeded this month's work order usage. Upgrade to Plus to keep roasting.",
      bodyPlus: "You've used the full Plus quota for this cycle. New cycle starts {{date}} — that's the highest tier we offer right now.",
      cycleUsage: 'This cycle',
      usage: '{{used}} / {{limit}} kg used',
      attempted: 'requested for this work order',
      resetsOn: 'Resets {{date}}',
      nextTierLine: {
        basic: 'Basic — 250 kg / month',
        plus: 'Plus — 1,000 kg / month',
      },
      cta: {
        upgradeToBasic: 'Upgrade to Basic',
        upgradeToPlus: 'Upgrade to Plus',
        dismiss: 'Close',
      },
    },
    machineTypeQuota: {
      title: 'Machine type usage',
      usedOf: '{used} / {limit} types',
      activeOf: '{active} active',
      unlimited: 'Unlimited',
      remaining: 'Remaining: {remaining}',
      upgradeForMore: 'Upgrade for more →',
      eyebrow: 'Machine types',
    },
    machineTypeLimitModal: {
      title: 'Machine type limit reached',
      eyebrow: 'Subscription limit',
      bodyFree:
        "You're at your free-tier machine type limit ({used}/{limit}). The cap counts every type — active or inactive — so you can't add another without deleting an existing one. Upgrade to Basic for up to 2.",
      bodyBasic:
        "You're at your basic-tier limit ({used}/{limit}). The cap counts every type — active or inactive. Upgrade to Plus for unlimited types.",
      reactivateNote:
        'Existing types stay saved. Delete one to free a slot, or upgrade.',
      cta: {
        upgradeToBasic: 'Upgrade to Basic',
        upgradeToPlus: 'Upgrade to Plus',
        dismiss: 'Dismiss',
      },
    },
    upgradeRequired: {
      artisanIntegration: {
        title: 'Upgrade required',
        body: 'Artisan integration requires the Basic tier or above. Upgrade to generate API keys.',
        cta: 'Go to Subscription',
      },
    },
    days_remaining: '{{days}} days left',
    current_tier: 'Current Tier',
    expires_on: 'Expires on',
    buy: {
      upgrade_to_basic: 'Upgrade to Basic',
      upgrade_to_plus: 'Upgrade to Plus',
    },
    claim_title: 'Claim a Voucher',
    claim_subtitle: 'Enter the code your administrator provided. The credit stacks on top of your current expiry.',
    code_placeholder: 'XXXX-XXXX-XXXX',
    redeem_cta: 'Redeem',
    history_title: 'Redemption History',
    history_subtitle: 'Every voucher redeemed for this company.',
    history_empty: 'No vouchers redeemed yet.',
    history: {
      redeemed_at: 'Claimed',
      months_added: 'Months',
      extended_to: 'New Expiry',
    },
    redeem: {
      success: 'Activated! +{{months}} months from {{name}}.',
      error_generic: 'Could not redeem voucher. Please verify the code and try again.',
    },
    upgrade_to_use: 'Upgrade to Basic to use this feature',
    voucher: {
      page_subtitle: 'Issue, audit, and manage subscription vouchers across the platform.',
      search_placeholder: 'Search by code or name…',
      stats: {
        total: 'Total',
        active: 'Active',
        expired: 'Expired',
        exhausted: 'Exhausted',
      },
      create_title: 'New Voucher',
      create_cta: 'New Voucher',
      list_empty: 'No vouchers found.',
      audit_title: 'Redemptions',
      audit_empty: 'No redemptions yet.',
      audit_count: '{{count}} redemption(s)',
      delete_title: 'Delete voucher?',
      delete_description: 'Code {{code}} will no longer be redeemable. Existing redemptions remain valid.',
      create_success: 'Voucher created',
      create_error: 'Failed to create voucher',
      delete_success: 'Voucher deleted',
      delete_error: 'Failed to delete voucher',
      confirm_delete: 'Soft-delete this voucher? Redemptions remain valid.',
      unlimited: 'Unlimited',
      filter: {
        all: 'All',
        active: 'Active',
        expired: 'Expired',
        exhausted: 'Exhausted',
        deleted: 'Deleted',
      },
      status: {
        active: 'Active',
        expired: 'Expired',
        exhausted: 'Exhausted',
        deleted: 'Deleted',
      },
      col: {
        code: 'Code',
        name: 'Name',
        duration: 'Duration',
        expires: 'Expires',
        usage: 'Usage',
        status: 'Status',
      },
      field: {
        name: 'Voucher name',
        code_optional: 'Code (optional — auto-generated if empty)',
        months: 'Months granted',
        max_redemptions: 'Max redemptions (blank = unlimited)',
      },
    },
    admin: {
      page_subtitle: 'Review tiers, expirations, and grant manual adjustments per company.',
      search_placeholder: 'Search company…',
      tier_filter: {
        all: 'All',
        free: 'Free',
        basic: 'Basic',
        plus: 'Plus',
      },
      stats: {
        total: 'Companies',
        free: 'On Free',
        basic: 'On Basic',
        plus: 'On Plus',
        expiring: 'Expiring in 7d',
      },
      empty: 'No companies match your filters.',
      col: {
        company: 'Company',
        tier: 'Tier',
        expires: 'Expires',
      },
      adjust_cta: 'Adjust',
      adjust_title: 'Adjust subscription',
      field: {
        months_delta: 'Months delta (positive extends, negative shortens)',
        tier_unchanged: 'Tier — leave as-is',
        reason: 'Reason (required for audit)',
        adjustment_type: 'Adjustment',
        adjustment_none: 'No month change',
        adjustment_add: 'Add months',
        adjustment_reduce: 'Reduce months',
        months_amount: 'Number of months',
      },
      validation: {
        nothing_changed: 'Provide a month change, a new tier, or both.',
        months_positive: 'Enter a positive number of months.',
      },
      confirm: {
        title: 'Confirm adjustment',
        intro: 'Please review the changes below. This action is logged and audited.',
        company: 'Company',
        current_tier: 'Current tier',
        new_tier: 'New tier',
        tier_unchanged: 'Unchanged',
        months_change: 'Subscription length',
        months_extend: 'Extend by {{count}} month(s)',
        months_shorten: 'Shorten by {{count}} month(s)',
        months_none: 'No change',
        expires_current: 'Current expiry',
        expires_new: 'New expiry (est.)',
        reason: 'Reason',
        back: 'Back',
        confirm_cta: 'Confirm & save',
      },
    },
  },
  labels: {
    downloadLabel: 'Download Label',
  },
};
